import CPRDrawArea from '@components/Lines/CPRDrawArea';
import CPRGuideLine from '@components/Lines/CPRGuideLine';
import { PickPoints } from 'config/analysisConfig';
import { useBranchInfo } from '@contexts/BranchContext';
import {
  CPRContext,
  handleNodeChange,
} from '@features/analysis/rendering/Panels/Renderers/vtkManagers';
import {
  registerRenderer,
  scheduleRender,
} from '@features/analysis/rendering/Panels/renderState';
import { usePanelContainer } from '@features/analysis/rendering/Panels/usePanelContainer';
import { useAnalysisPage } from '@features/analysis/useAnalysisPage';
import { exportCPRLabelMap } from 'utils/CPRUtilities/CPRUtilities';
import { runWorkerTask } from '@features/analysis/rendering/Panels/workerPool';
import { useBufferPool } from '@features/analysis/rendering/Panels/Renderers/vtkManagers';
import {
  applyVoxelIJKEditToSegmentManager,
  commitVoxelIJKEditToSegmentManagerHistory,
  subscribeSegmentHistoryChange,
} from '@features/analysis/segmentManager';
import { useBrowserPage } from '@features/browser/useBrowserPage';
import {
  applyLocalBoundaryDeformation,
  buildBoundaryModelFromLabelMask,
  cloneBoundaryModel,
  cloneBoundarySnapshot,
  controlPointsToLocal,
  CprBoundaryModel,
  CprBoundarySnapshot,
  modelToLocalOutline,
  rasterizeBoundaryModel,
  syncControlPointsFromDense,
} from 'utils/CPRUtilities/cprBoundaryUtils';
import {
  buildSmoothOpenPath,
  clearCanvas,
  createSolidMaskFillCanvas,
  drawMaskFillCanvas,
  projectLocalOutlinesToScreen,
  projectLocalPointToScreen,
} from 'utils/CPRUtilities/cprOverlayUtils';
import { notifyCprBoundaryEditHistoryChanged } from 'utils/CPRUtilities/cprBoundaryEditHistory';
import { useConfig } from 'config/useConfig';
import React, { useEffect, useLayoutEffect, useReducer, useRef } from 'react';
import type {
  BoundaryEditTarget,
  BoundaryFrame,
  BoundaryScreenAxis,
  BoundaryScreenHit,
  BoundarySide,
  BoundarySculptStartRegion,
  BoundarySculptSession,
  CprBoundaryEditMode,
  CprPixelBrushMode,
  CprPixelBrushSession,
  LabelDataApplyVoxelCollection,
  PathPoints,
  PerpendicularLine,
  SolidFootprintOptions,
  SolidLumenVolumeVoxelCollection,
  Vec3,
  VoxelIJKChange,
  VoxelIJKCollection,
} from 'utils/CPRUtilities/markerLineTypes';
import {
  areMasksEqual,
  clampValue,
  createRawLumenMaskFromLabelMap,
  getPolyPoints,
  getVoxelKey,
  mergeVoxelChanges,
} from 'utils/CPRUtilities/markerLineUtils';
import {
  collectLocalCurrentLumenIndices as collectLocalCurrentLumenIndicesCore,
  collectSolidFootprintVoxelIJK as collectSolidFootprintVoxelIJKCore,
  collectVoxelIJK as collectVoxelIJKCore,
  expandVoxelIJKForLabelApply as expandVoxelIJKForLabelApplyCore,
  getBoundaryRowAxis as getBoundaryRowAxisCore,
} from 'utils/CPRUtilities/cprVoxelUtils';
import {
  cprScreenPointToWorld as cprScreenPointToWorldCore,
  focusLinkedViewsFromCpr,
} from 'utils/CPRUtilities/cprFocusSyncUtils';
import { createCprLumenVolumeVoxelCollectors } from 'utils/CPRUtilities/cprLumenVolumeVoxelUtils';

import {
  applyVoxelChangesToLabelData as applyVoxelChangesToLabelDataCore,
  restoreLumenLabelDataApplyPreview as restoreLumenLabelDataApplyPreviewCore,
  restoreLumenLabelDataApplyPreviewForChanges as restoreLumenLabelDataApplyPreviewForChangesCore,
} from '../../utils/CPRUtilities/cprLabelDataApply';
import {
  applyCprPixelBrushMaskEdit,
  collectCprPixelBrushMaskIndices as collectCprPixelBrushMaskIndicesCore,
  screenPointToLabelMapPixel as screenPointToLabelMapPixelCore,
} from '../../utils/CPRUtilities/cprPixelBrushUtils';
import {
  findBoundaryScreenHit as findBoundaryScreenHitCore,
  getBoundarySculptAxis as getBoundarySculptAxisCore,
} from '../../utils/CPRUtilities/cprBoundarySculptUtils';
import { useCprBoundaryHistoryRegistration } from '../../utils/CPRUtilities/useCprBoundaryHistoryRegistration';

function clampMiddleIndexBetweenBounds(
  candidate: number,
  proximalIndex: number,
  distalIndex: number,
) {
  const proximal = Math.round(proximalIndex);
  const distal = Math.round(distalIndex);
  const min = Math.min(proximal, distal);
  const max = Math.max(proximal, distal);

  if (max - min > 1) {
    return Math.max(min + 1, Math.min(max - 1, Math.round(candidate)));
  }

  return Math.max(min, Math.min(max, Math.round(candidate)));
}

export interface MarkerLineProps {
  mode: string;
  mainIndex: number;
  centerlineData: any;
  dimensions: { width: number; height: number };
  branchId?: number;
  pickPoints: PickPoints[];
  numPairs?: number;
  context: CPRContext;
}

export default function MarkerLine({
  mode,
  mainIndex,
  centerlineData,
  dimensions,
  branchId,
  pickPoints,
  numPairs = 1,
  context,
}: MarkerLineProps) {
  const hasValidCenterline = (data: any): boolean => {
    if (!data || typeof data.getPoints !== 'function') return false;
    const pts = data.getPoints?.();
    return !!pts && typeof pts.getNumberOfPoints === 'function';
  };

  const getSafeCenterlineNumPoints = (data: any): number => {
    if (!hasValidCenterline(data)) return 0;
    const pts = data.getPoints?.();
    const n = pts?.getNumberOfPoints?.();
    return Number.isFinite(n) ? Number(n) : 0;
  };

  const overlayTranslation = context.overlayTranslation;
  const { config } = useConfig();
  const { bufferPool } = useBufferPool();
  const { selectedSeriesId } = useBrowserPage();

  const refLumenCanvas = useRef<HTMLCanvasElement>(null);
  const refAnnulusCanvas = useRef<HTMLCanvasElement>(null);
  const refSVG = useRef<SVGSVGElement>(null);
  const refCursor = useRef<{ index: number; cursor: string }>({
    index: 99,
    cursor: 'default',
  });

  const path = useRef<{ x: number; y: number }[]>([]);
  const dragMarker = useRef<number[]>([]);
  const startY = useRef<number>();
  const refPerpendicularLines = useRef<PerpendicularLine[]>([]);
  const refPathPoints = useRef<PathPoints[]>([]);
  const refPolygonPoints = useRef<string[]>([]);
  const forceUpdateRaf = useRef<number | null>(null);

  const refLocalLumenOutlines = useRef<[number, number][][]>([]);
  const refLocalWallOutlines = useRef<[number, number][][]>([]);
  const refLumenOutlineSegments = useRef<PathPoints[][]>([]);
  const refWallOutlineSegments = useRef<PathPoints[][]>([]);

  const refOutlineCacheKey = useRef<string>('');
  const refOutlineTimer = useRef<number | null>(null);
  const refOutlinePerfSeq = useRef(0);
  const refOutlineBusy = useRef(false);
  const refOutlineDirty = useRef(false);

  const refBoundaryModel = useRef<CprBoundaryModel | null>(null);
  const refWallBoundaryModel = useRef<CprBoundaryModel | null>(null);
  const refBoundarySpacing = useRef<{ x: number; y: number }>({ x: 1, y: 1 });
  const refBoundaryFrame = useRef<BoundaryFrame | null>(null);
  const refLumenBoundaryMask = useRef<Uint8Array | null>(null);
  const refBoundaryMask = useRef<Uint8Array | null>(null);
  const refInitialLumenMask = useRef<Uint8Array | null>(null);
  const refInitialWallMask = useRef<Uint8Array | null>(null);
  const refCumulativeChangedMaskIndices = useRef<Set<number>>(new Set());
  const refLumenAddedMaskIndices = useRef<Set<number>>(new Set());
  const refLumenRemovedMaskIndices = useRef<Set<number>>(new Set());
  const refLumenAddedVoxelIJK = useRef<VoxelIJKChange[]>([]);
  const refLumenRemovedVoxelIJK = useRef<VoxelIJKChange[]>([]);
  const refLumenAppliedOriginalValues = useRef<Map<string, number>>(new Map());
  const refManualModelSourceId = useRef<string | null>(null);
  const refManualModelAddedVoxelMap = useRef<Map<string, VoxelIJKChange>>(new Map());
  const refManualModelRemovedVoxelMap = useRef<Map<string, VoxelIJKChange>>(new Map());

  const refUndoStack = useRef<CprBoundarySnapshot[]>([]);
  const refRedoStack = useRef<CprBoundarySnapshot[]>([]);
  const refLumenUndoStack = useRef<CprBoundarySnapshot[]>([]);
  const refLumenRedoStack = useRef<CprBoundarySnapshot[]>([]);

  const refLumenControlScreen = useRef<{
    left: PathPoints[];
    right: PathPoints[];
  }>({ left: [], right: [] });
  const refControlScreen = useRef<{
    left: PathPoints[];
    right: PathPoints[];
  }>({ left: [], right: [] });
  const refBaseLumenOutlineSegments = useRef<PathPoints[][]>([]);
  const refBaseWallOutlineSegments = useRef<PathPoints[][]>([]);
  const refBaseLumenControlScreen = useRef<{
    left: PathPoints[];
    right: PathPoints[];
  }>({ left: [], right: [] });
  const refBaseControlScreen = useRef<{ left: PathPoints[]; right: PathPoints[] }>({
    left: [],
    right: [],
  });
  const refBaseAnnulusCorners = useRef<PathPoints[] | null>(null);
  const refDisplayOverlayOffset = useRef<{ x: number; y: number }>({ x: 0, y: 0 });

  const refLastOverlayScreen = useRef<{ x: number; y: number } | null>(null);
  const refLastEditedBoundaryTarget = useRef<BoundaryEditTarget>('plaque');

  const refDraggingControl = useRef<{
    target: BoundaryEditTarget;
    side: BoundarySide;
    index: number;
  } | null>(null);

  const refBoundaryDragSession = useRef<{
    target: BoundaryEditTarget;
    side: BoundarySide;
    index: number;
    anchorRow: number;
    startScreenX: number;
    startScreenY: number;
    axisUx: number;
    axisUy: number;
    axisPxPerCol: number;
    baseLeftX: number[];
    baseRightX: number[];
    baseMask: Uint8Array;
  } | null>(null);
  const refBoundarySculptSession = useRef<BoundarySculptSession | null>(null);
  const refBoundarySculptCursor = useRef<{
    x: number;
    y: number;
    visible: boolean;
    active: boolean;
  }>({ x: 0, y: 0, visible: false, active: false });
  const refBoundarySculptPendingPoint = useRef<PathPoints | null>(null);
  const refBoundarySculptFrame = useRef<number | null>(null);
  const refCprPixelBrushSession = useRef<CprPixelBrushSession | null>(null);
  const refCprPixelBrushCursor = useRef<{
    x: number;
    y: number;
    visible: boolean;
    active: boolean;
  }>({ x: 0, y: 0, visible: false, active: false });

  const refLabelMap = useRef<{
    width: number;
    height: number;
    spacingX: number;
    spacingY: number;
    data: Uint8Array;
  } | null>(null);

  const refLumenSourceCanvas = useRef<HTMLCanvasElement | null>(null);
  const refAnnulusSourceCanvas = useRef<HTMLCanvasElement | null>(null);

  const LOCAL_DEFORM_CONFIG = {
    radiusRows: 8,
    sigmaRows: 5,
    minThickness: 1.2,
    smoothRadiusRows: 2,
  };

  const BOUNDARY_SCULPT_SIZE = Math.max(
    1,
    Math.min(20, Number(config.cprSculptSize) || 5),
  );
  const BOUNDARY_SCULPT_RADIUS_PX = BOUNDARY_SCULPT_SIZE * 4;
  const BOUNDARY_DIRECT_DRAG_RADIUS_PX = 8;
  const BOUNDARY_SCULPT_PRESSURE = 0.82;
  const BOUNDARY_SCULPT_COLOR = '#2f80ff';
  const CPR_PIXEL_BRUSH_SIZE = Math.max(
    1,
    Math.min(30, Number(config.cprPixelBrushSize) || 5),
  );
  const CPR_PIXEL_BRUSH_RADIUS_PX = CPR_PIXEL_BRUSH_SIZE;
  const CPR_PIXEL_BRUSH_MODE: CprPixelBrushMode =
    config.cprPixelBrushMode === 'erase' ? 'erase' : 'draw';
  const CPR_PIXEL_BRUSH_COLOR =
    CPR_PIXEL_BRUSH_MODE === 'erase' ? '#ff5f5f' : '#2ee6ff';

  const LUMEN_PREVIEW_RGBA = [255, 155, 190, 185] as const;
  const refPaintActorOriginalVisibility = useRef<boolean | null>(null);

  const refPickPoints = useRef<number[]>([]);
  useEffect(() => {
    refPickPoints.current = pickPoints.map((p) => p.index);
  }, [pickPoints]);

  const [, forceUpdate] = useReducer((x) => x + 1, 0);

  const requestForceUpdate = () => {
    if (forceUpdateRaf.current !== null) return;
    forceUpdateRaf.current = window.requestAnimationFrame(() => {
      forceUpdateRaf.current = null;
      forceUpdate();
    });
  };

  const { mainBranchIds } = useBranchInfo();
  const { analysisType, updateCPRState } = usePanelContainer();
  const { selectedLayoutConfig } = useAnalysisPage();
  const analysisTypeKey = analysisType as string;
  const normalizeBoundaryMode = (value: unknown): CprBoundaryEditMode =>
    value === 'lumen' || value === 'plaque' ? value : 'none';
  const configuredBoundaryMode = normalizeBoundaryMode(config.EditMode);
  const isLesionStackMarkerMode =
    analysisType === 'plaque' &&
    selectedLayoutConfig.type === 'lesionStackView' &&
    numPairs === 3;
  const isCprBoundaryTarget = mode === 'straightened';
  const activeBoundaryMode = isCprBoundaryTarget
    ? configuredBoundaryMode
    : 'none';
  const isBoundaryDisplayMode = activeBoundaryMode !== 'none';
  const activeCprModificationTool =
    isBoundaryDisplayMode && config.cprModificationTool
      ? config.cprModificationTool
      : 'none';
  const isBoundaryLineTool = activeCprModificationTool === 'line';
  const isBoundarySculptTool = activeCprModificationTool === 'sculpt';
  const isCprPixelBrushTool = activeCprModificationTool === 'brush';
  const isLumenBoundaryMode = activeBoundaryMode === 'lumen';
  const isLumenLabelVisible = config.label.lumen !== false;
  const isLumenOutlineVisible = config.label.lumenoutline !== false;
  const showLumenBoundaryFillOverlay =
  isLumenBoundaryMode &&
  isLumenLabelVisible &&
  (
    config.EditMode !== 'lumen' ||
    config.manualLabelmapStyle === 'fill'
  );
   const showLumenBoundaryOverlay = showLumenBoundaryFillOverlay;
  const showLumenBoundaryShapeOverlay =
    isLumenBoundaryMode &&
    isLumenOutlineVisible &&
    (!isCprPixelBrushTool ||
      config.EditMode !== 'lumen' ||
      config.manualLabelmapStyle === 'outline');
  const showPlaqueBoundaryOverlay = false;
  const showLegacyMarkerLineOverlays = !isBoundaryDisplayMode;

  useEffect(() => {
    if (isBoundarySculptTool) return;
    if (!refBoundarySculptCursor.current.visible) return;
    refBoundarySculptCursor.current = {
      ...refBoundarySculptCursor.current,
      visible: false,
      active: false,
    };
    requestForceUpdate();
  }, [isBoundarySculptTool]);

  useEffect(() => {
    if (isCprPixelBrushTool) return;
    if (!refCprPixelBrushCursor.current.visible) return;
    refCprPixelBrushCursor.current = {
      ...refCprPixelBrushCursor.current,
      visible: false,
      active: false,
    };
    requestForceUpdate();
  }, [isCprPixelBrushTool]);

  const getCenterOffsetY = () =>
    mode === 'stretched' && refPathPoints.current.length
      ? dimensions.height / 2 -
        refPathPoints.current[Math.floor(refPathPoints.current.length / 2)].y
      : 0;

  const getLiveOverlayTranslation = () =>
    context.overlayTranslation ?? overlayTranslation ?? [0, 0];

  const getOverlayScreenPosition = () => {
    const liveOverlay =
      getLiveOverlayTranslation();
    return {
      x: Number(liveOverlay[0]) || 0,
      y: (Number(liveOverlay[1]) || 0) + getCenterOffsetY(),
    };
  };

  const getReprojectScreenOffset = (renderer?: any, paintActor?: any) => {
    const liveOverlay = getLiveOverlayTranslation();
    const overlayX = Number(liveOverlay[0]) || 0;
    const overlayY = Number(liveOverlay[1]) || 0;
    const fallback = {
      x: 0,
      y: overlayY * 2,
    };

    const labelMap = refLabelMap.current;
    const pathPoints = refPathPoints.current;
    const spacing = refBoundarySpacing.current;
    if (
      !renderer ||
      !paintActor ||
      !labelMap ||
      pathPoints.length < 2 ||
      !Number.isFinite(spacing.x) ||
      !Number.isFinite(spacing.y) ||
      spacing.x <= 0 ||
      spacing.y <= 0
    ) {
      return fallback;
    }
    if (Math.abs(overlayX) < 0.5 && Math.abs(overlayY) < 0.5) {
      return fallback;
    }

    const model = refBoundaryModel.current ?? refWallBoundaryModel.current;
    const rowStart = model?.rowStart ?? 0;
    const rowEnd = model?.rowEnd ?? labelMap.height - 1;
    const rowSpan = Math.max(1, rowEnd - rowStart);
    const sampleRows = [
      rowStart + rowSpan * 0.25,
      rowStart + rowSpan * 0.5,
      rowStart + rowSpan * 0.75,
    ].map((row) =>
      Math.max(0, Math.min(labelMap.height - 1, Math.round(row))),
    );

    const dxValues: number[] = [];
    const dyValues: number[] = [];
    const centerLocalX = labelMap.width * spacing.x * 0.5;
    const centerOffsetY = getCenterOffsetY();

    sampleRows.forEach((row) => {
      const projected = projectLocalPoint(
        centerLocalX,
        (row + 0.5) * spacing.y,
        renderer,
        paintActor,
      );
      if (!projected) return;

      const pathIndex = Math.max(
        0,
        Math.min(
          pathPoints.length - 1,
          Math.round(
            (row / Math.max(1, labelMap.height - 1)) *
              (pathPoints.length - 1),
          ),
        ),
      );
      const pathPoint = pathPoints[pathIndex];
      if (!pathPoint) return;

      dxValues.push(pathPoint.x + overlayX - projected.x);
      dyValues.push(pathPoint.y + overlayY + centerOffsetY - projected.y);
    });

    if (!dxValues.length || !dyValues.length) return fallback;

    const median = (values: number[]) => {
      const sorted = values
        .filter((value) => Number.isFinite(value))
        .sort((a, b) => a - b);
      return sorted.length
        ? sorted[Math.floor(sorted.length / 2)]
        : Number.NaN;
    };
    const x = median(dxValues);
    const y = median(dyValues);

    return {
      x: Number.isFinite(x) ? x : fallback.x,
      y: Number.isFinite(y) ? y : fallback.y,
    };
  };
  const isAorticMode =
    analysisType === 'aorticValveCalcium' || analysisTypeKey === 'tavi';
  const pool = selectedSeriesId ? bufferPool[selectedSeriesId] : undefined;

  function toSVGPoint({
    clientX,
    clientY,
  }: {
    clientX: number;
    clientY: number;
  }) {
    const pt = refSVG.current?.createSVGPoint()!;
    pt.x = clientX;
    pt.y = clientY;
    return pt.matrixTransform(refSVG.current?.getScreenCTM()!.inverse());
  }

  function findNearestPath(
    svgPoint: DOMPoint,
    pathValue: { x: number; y: number }[],
  ) {
    const ty = overlayTranslation?.[1] || 0;
    const px = svgPoint.x;
    const py = svgPoint.y - ty;

    const d2 = (i: number) => {
      const p = pathValue[i];
      const dx = p.x - px;
      const dy = p.y - py;
      return dx * dx + dy * dy;
    };

    let l = 0;
    let r = pathValue.length - 1;
    if (r < 3) {
      let bi = 0;
      let bd = Infinity;
      for (let i = 0; i <= r; ++i) {
        const v = d2(i);
        if (v < bd) {
          bd = v;
          bi = i;
        }
      }
      return bi;
    }

    while (r - l > 3) {
      const m1 = l + (((r - l) / 3) | 0);
      const m2 = r - (((r - l) / 3) | 0);
      d2(m1) > d2(m2) ? (l = m1 + 1) : (r = m2 - 1);
    }

    let best = l;
    let bestV = d2(l);
    for (let i = l + 1; i <= r; ++i) {
      const v = d2(i);
      if (v < bestV) {
        bestV = v;
        best = i;
      }
    }
    return best;
  }

  function findNearestScreenPointIndex(
    svgPoint: DOMPoint,
    points: PathPoints[],
  ) {
    if (!points.length) return -1;
    let best = 0;
    let bestD = Infinity;
    for (let i = 0; i < points.length; i += 1) {
      const dx = points[i].x - svgPoint.x;
      const dy = points[i].y - svgPoint.y;
      const d = dx * dx + dy * dy;
      if (d < bestD) {
        bestD = d;
        best = i;
      }
    }
    return best;
  }

  const projectLocalPoint = (
    x: number,
    y: number,
    renderer: any,
    targetActor: any,
  ): PathPoints | null => {
    return projectLocalPointToScreen({
      x,
      y,
      renderer,
      targetActor,
      dimensions,
    });
  };

  const projectLocalOutlines = (
    localOutlines: [number, number][][],
    renderer: any,
    targetActor: any,
  ): PathPoints[][] => {
    return projectLocalOutlinesToScreen(
      localOutlines,
      renderer,
      targetActor,
      dimensions,
    );
  };

  const rebuildLumenSourceCanvas = () => {
    const labelMap = refLabelMap.current;
    const mask = isCprPixelBrushTool
      ? labelMap
        ? createRawLumenMaskFromLabelMap(labelMap)
        : null
      : refLumenBoundaryMask.current ??
        (refBoundaryModel.current
          ? rasterizeBoundaryModel(refBoundaryModel.current)
          : null);

    if (!labelMap || !mask) {
      refLumenSourceCanvas.current = null;
      return;
    }

    refLumenSourceCanvas.current = createSolidMaskFillCanvas({
      labelMap,
      mask,
      rgba: LUMEN_PREVIEW_RGBA,
    });
  };

  const drawLumenOverlayCanvas = () => {
    const canvas = refLumenCanvas.current;
    const labelMap = refLabelMap.current;
    const src = refLumenSourceCanvas.current;

    if (!canvas) return;

    if (!labelMap || !src) {
      clearCanvas(canvas);
      return;
    }

    drawMaskFillCanvas({
      canvas,
      dimensions,
      labelMap,
      sourceCanvas: src,
      baseCorners: refBaseAnnulusCorners.current,
      offset: refDisplayOverlayOffset.current,
      clipSegments: isCprPixelBrushTool
        ? undefined
        : refLumenOutlineSegments.current,
    });
  };

  useEffect(() => {
    if (!showLumenBoundaryFillOverlay) return;
    const raf = window.requestAnimationFrame(() => {
      drawLumenOverlayCanvas();
    });
    return () => window.cancelAnimationFrame(raf);
  }, [
    showLumenBoundaryFillOverlay,
    dimensions.width,
    dimensions.height,
    isCprPixelBrushTool,
  ]);


  const {
    collectManualAddedVoxelsOutsideCurrentBoundary,
    collectSolidLumenVolumeVoxelIJK,
    collectConicalLumenDeltaVoxelIJK,
  } = createCprLumenVolumeVoxelCollectors({
    getBoundaryFrame: () => refBoundaryFrame.current,
    getLabelData: () => context.labelData as any,
    getBoundaryModel: () => refBoundaryModel.current,
    getManualModelAddedVoxelMap: () => refManualModelAddedVoxelMap.current,
  });

  const rebuildAnnulusSourceCanvas = () => {
    refAnnulusSourceCanvas.current = null;
  };

  const drawAnnulusOverlayCanvas = () => {
    const canvas = refAnnulusCanvas.current;
    if (canvas) clearCanvas(canvas);
  };

  const drawBoundaryOverlayCanvases = () => {
    drawLumenOverlayCanvas();
    drawAnnulusOverlayCanvas();
  };

  const applyScreenOverlayOffset = (dx: number, dy: number) => {
    refDisplayOverlayOffset.current = { x: dx, y: dy };
    refLumenOutlineSegments.current = refBaseLumenOutlineSegments.current.map((seg) =>
      seg.map((p) => ({ x: p.x + dx, y: p.y + dy })),
    );
    refWallOutlineSegments.current = refBaseWallOutlineSegments.current.map((seg) =>
      seg.map((p) => ({ x: p.x + dx, y: p.y + dy })),
    );
    refLumenControlScreen.current = {
      left: refBaseLumenControlScreen.current.left.map((p) => ({
        x: p.x + dx,
        y: p.y + dy,
      })),
      right: refBaseLumenControlScreen.current.right.map((p) => ({
        x: p.x + dx,
        y: p.y + dy,
      })),
    };
    refControlScreen.current = {
      left: refBaseControlScreen.current.left.map((p) => ({
        x: p.x + dx,
        y: p.y + dy,
      })),
      right: refBaseControlScreen.current.right.map((p) => ({
        x: p.x + dx,
        y: p.y + dy,
      })),
    };
    drawBoundaryOverlayCanvases();
  };

  const shiftOverlayGeometryBy = (dx: number, dy: number) => {
    if (!Number.isFinite(dx) || !Number.isFinite(dy)) return;
    const prev = refDisplayOverlayOffset.current;
    applyScreenOverlayOffset(prev.x + dx, prev.y + dy);
  };

  const applyBoundaryModelToScene = (renderer: any, paintActor: any) => {
    const spacing = refBoundarySpacing.current;
    const lumenModel = refBoundaryModel.current;
    const wallModel = refWallBoundaryModel.current;

    if (lumenModel) {
      const lumenLocalOutlines = modelToLocalOutline(
        lumenModel,
        spacing.x,
        spacing.y,
      );
      refLocalLumenOutlines.current = lumenLocalOutlines;
      refBaseLumenOutlineSegments.current = projectLocalOutlines(
        lumenLocalOutlines,
        renderer,
        paintActor,
      );
    } else {
      refLocalLumenOutlines.current = [];
      refBaseLumenOutlineSegments.current = [];
    }

    if (wallModel) {
      const wallLocalOutlines = modelToLocalOutline(
        wallModel,
        spacing.x,
        spacing.y,
      );
      refLocalWallOutlines.current = wallLocalOutlines;
      refBaseWallOutlineSegments.current = projectLocalOutlines(
        wallLocalOutlines,
        renderer,
        paintActor,
      );
    } else {
      refLocalWallOutlines.current = [];
      refBaseWallOutlineSegments.current = [];
    }

    const localLumenLeftControls = lumenModel
      ? controlPointsToLocal(
          lumenModel.leftControlPoints,
          spacing.x,
          spacing.y,
        )
      : [];
    const localLumenRightControls = lumenModel
      ? controlPointsToLocal(
          lumenModel.rightControlPoints,
          spacing.x,
          spacing.y,
        )
      : [];
    const localWallLeftControls = wallModel
      ? controlPointsToLocal(wallModel.leftControlPoints, spacing.x, spacing.y)
      : [];
    const localWallRightControls = wallModel
      ? controlPointsToLocal(
          wallModel.rightControlPoints,
          spacing.x,
          spacing.y,
        )
      : [];

    const projectSingle = (p: { x: number; y: number }) =>
      projectLocalOutlines([[[p.x, p.y]]], renderer, paintActor)?.[0]?.[0];

    refBaseLumenControlScreen.current = {
      left: localLumenLeftControls
        .map((p) => projectSingle(p))
        .filter((p): p is PathPoints => !!p),
      right: localLumenRightControls
        .map((p) => projectSingle(p))
        .filter((p): p is PathPoints => !!p),
    };
    refBaseControlScreen.current = {
      left: localWallLeftControls
        .map((p) => projectSingle(p))
        .filter((p): p is PathPoints => !!p),
      right: localWallRightControls
        .map((p) => projectSingle(p))
        .filter((p): p is PathPoints => !!p),
    };

    const labelMap = refLabelMap.current;
    refBaseAnnulusCorners.current =
      labelMap && renderer && paintActor
        ? (projectLocalOutlines(
            [[
              [0, 0],
              [labelMap.width * labelMap.spacingX, 0],
              [0, labelMap.height * labelMap.spacingY],
            ]],
            renderer,
            paintActor,
          )[0] ?? null)
        : null;

    refLastOverlayScreen.current = getOverlayScreenPosition();
    const reprojectOffset = getReprojectScreenOffset(renderer, paintActor);
    applyScreenOverlayOffset(reprojectOffset.x, reprojectOffset.y);
  };

  const getProjectionActor = () =>
    (context.actorIds['CPRActor'] as any) ??
    (context.actorIds['CPRPaintActor'] as any);

  const clearCprOutlines = () => {
    refLocalLumenOutlines.current = [];
    refLocalWallOutlines.current = [];
    refLumenOutlineSegments.current = [];
    refWallOutlineSegments.current = [];
    refBoundaryModel.current = null;
    refLumenBoundaryMask.current = null;
    refBoundaryMask.current = null;
    refInitialLumenMask.current = null;
    refInitialWallMask.current = null;
    refCumulativeChangedMaskIndices.current = new Set();
    refLumenAddedMaskIndices.current = new Set();
    refLumenRemovedMaskIndices.current = new Set();
    refLumenAddedVoxelIJK.current = [];
    refLumenRemovedVoxelIJK.current = [];
    refLumenAppliedOriginalValues.current.clear();
    if (refBoundarySculptFrame.current !== null) {
      window.cancelAnimationFrame(refBoundarySculptFrame.current);
      refBoundarySculptFrame.current = null;
    }
    refBoundarySculptPendingPoint.current = null;
    refBoundarySculptSession.current = null;
    refBoundarySculptCursor.current = {
      x: 0,
      y: 0,
      visible: false,
      active: false,
    };
    refCprPixelBrushSession.current = null;
    refCprPixelBrushCursor.current = {
      x: 0,
      y: 0,
      visible: false,
      active: false,
    };
    refWallBoundaryModel.current = null;
    refLabelMap.current = null;
    refLumenSourceCanvas.current = null;
    refAnnulusSourceCanvas.current = null;
    refLumenControlScreen.current = { left: [], right: [] };
    refControlScreen.current = { left: [], right: [] };
    refBaseLumenOutlineSegments.current = [];
    refBaseWallOutlineSegments.current = [];
    refBaseLumenControlScreen.current = { left: [], right: [] };
    refBaseControlScreen.current = { left: [], right: [] };
    refBaseAnnulusCorners.current = null;
    refDisplayOverlayOffset.current = { x: 0, y: 0 };
    refOutlineCacheKey.current = '';
    refLastOverlayScreen.current = null;
    refLumenUndoStack.current = [];
    refLumenRedoStack.current = [];
    refUndoStack.current = [];
    refRedoStack.current = [];
    notifyCprBoundaryEditHistoryChanged();

    const lumenCanvas = refLumenCanvas.current;
    if (lumenCanvas) {
      clearCanvas(lumenCanvas);
    }
    const canvas = refAnnulusCanvas.current;
    if (canvas) {
      clearCanvas(canvas);
    }
  };

  const getBoundaryModel = (target: BoundaryEditTarget) =>
    target === 'lumen'
      ? refBoundaryModel.current
      : refWallBoundaryModel.current;

  const setBoundaryModel = (
    target: BoundaryEditTarget,
    model: CprBoundaryModel,
  ) => {
    if (target === 'lumen') {
      refBoundaryModel.current = model;
    } else {
      refWallBoundaryModel.current = model;
    }
  };

  const setBoundaryMask = (
    target: BoundaryEditTarget,
    mask: Uint8Array | null,
  ) => {
    if (target === 'lumen') {
      refLumenBoundaryMask.current = mask;
    } else {
      refBoundaryMask.current = mask;
    }
  };

  const getInitialBoundaryMask = (target: BoundaryEditTarget) =>
    target === 'lumen'
      ? refInitialLumenMask.current
      : refInitialWallMask.current;

  const getUndoStack = (target: BoundaryEditTarget) =>
    target === 'lumen' ? refLumenUndoStack.current : refUndoStack.current;

  const getRedoStack = (target: BoundaryEditTarget) =>
    target === 'lumen' ? refLumenRedoStack.current : refRedoStack.current;

  const setRedoStack = (
    target: BoundaryEditTarget,
    stack: CprBoundarySnapshot[],
  ) => {
    if (target === 'lumen') {
      refLumenRedoStack.current = stack;
    } else {
      refRedoStack.current = stack;
    }
  };

  const canEditBoundaryTarget = (target: BoundaryEditTarget) =>
    activeBoundaryMode === 'plaque' ||
    (activeBoundaryMode === 'lumen' && target === 'lumen');

  const pushSnapshot = (target: BoundaryEditTarget) => {
    const model = getBoundaryModel(target);
    if (!model) return;
    const mask = rasterizeBoundaryModel(model);
    setBoundaryMask(target, mask);
    const undoStack = getUndoStack(target);
    undoStack.push(
      cloneBoundarySnapshot({
        model: cloneBoundaryModel(model),
        mask,
      }),
    );
    if (undoStack.length > 50) {
      undoStack.shift();
    }
    setRedoStack(target, []);
    notifyCprBoundaryEditHistoryChanged();
  };

  const pushCprPixelBrushSnapshot = ({
    resetHistoryBaseline = false,
  }: { resetHistoryBaseline?: boolean } = {}) => {
    const labelMap = refLabelMap.current;
    if (!labelMap) return false;

    const mask = createRawLumenMaskFromLabelMap(labelMap);
    const model =
      buildBoundaryModelFromLabelMask(
        mask,
        labelMap.width,
        labelMap.height,
        labelMap.width * 0.5,
      ) ?? refBoundaryModel.current;
    if (!model) return false;

    refBoundaryModel.current = cloneBoundaryModel(model);
    refLumenBoundaryMask.current = new Uint8Array(mask);
    refLastEditedBoundaryTarget.current = 'lumen';

    const snapshot = cloneBoundarySnapshot({
      model: cloneBoundaryModel(model),
      mask,
    });

    if (resetHistoryBaseline) {
      refInitialLumenMask.current = new Uint8Array(mask);
      refLumenUndoStack.current = [snapshot];
      refLumenRedoStack.current = [];
      notifyCprBoundaryEditHistoryChanged();
      return true;
    }

    const undoStack = getUndoStack('lumen');
    const previous = undoStack[undoStack.length - 1];
    if (previous && areMasksEqual(previous.mask, mask)) return false;

    undoStack.push(snapshot);
    if (undoStack.length > 50) {
      undoStack.shift();
    }
    setRedoStack('lumen', []);
    notifyCprBoundaryEditHistoryChanged();
    return true;
  };

  const ensureCprPixelBrushUndoBaseline = () => {
    const labelMap = refLabelMap.current;
    if (!labelMap) return false;

    const mask = createRawLumenMaskFromLabelMap(labelMap);
    const undoStack = getUndoStack('lumen');
    const current = undoStack[undoStack.length - 1];

    // Pixel Brush must not undo into the Line/Sculpt boundary-fill baseline.
    // When the current history top already matches raw labelData, keep the
    // existing stack so multiple Brush strokes can still be undone one by one.
    if (current && areMasksEqual(current.mask, mask)) return true;

    const model =
      buildBoundaryModelFromLabelMask(
        mask,
        labelMap.width,
        labelMap.height,
        labelMap.width * 0.5,
      ) ?? refBoundaryModel.current;
    if (!model) return false;

    const snapshot = cloneBoundarySnapshot({
      model: cloneBoundaryModel(model),
      mask: new Uint8Array(mask),
    });

    refBoundaryModel.current = cloneBoundaryModel(model);
    refLumenBoundaryMask.current = new Uint8Array(mask);
    refInitialLumenMask.current = new Uint8Array(mask);
    refLumenAddedMaskIndices.current = new Set();
    refLumenRemovedMaskIndices.current = new Set();
    refLumenAddedVoxelIJK.current = [];
    refLumenRemovedVoxelIJK.current = [];
    refLastEditedBoundaryTarget.current = 'lumen';

    // Replace, don't append. If the older outline-derived snapshot remains below
    // the raw baseline, repeated Undo in Brush mode can fall back to the smoothed
    // SVG/boundary fill or appear as if the label overlay was turned off.
    refLumenUndoStack.current = [snapshot];
    refLumenRedoStack.current = [];
    notifyCprBoundaryEditHistoryChanged();
    return true;
  };

  const buildClosedBoundaryFillPath = (segments: PathPoints[][]) => {
    const left = segments[0] ?? [];
    const right = segments[1] ?? [];
    if (left.length < 2 || right.length < 2) return '';

    const appendSmoothContinuation = (points: PathPoints[]) => {
      if (points.length < 2) return '';
      if (points.length === 2) {
        return ` L ${points[1].x} ${points[1].y}`;
      }

      let d = '';
      for (let i = 0; i < points.length - 1; i += 1) {
        const p0 = points[i - 1] ?? points[i];
        const p1 = points[i];
        const p2 = points[i + 1];
        const p3 = points[i + 2] ?? p2;

        const cp1x = p1.x + (p2.x - p0.x) / 6;
        const cp1y = p1.y + (p2.y - p0.y) / 6;
        const cp2x = p2.x - (p3.x - p1.x) / 6;
        const cp2y = p2.y - (p3.y - p1.y) / 6;

        d += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2.x} ${p2.y}`;
      }
      return d;
    };

    const reversedRight = [...right].reverse();
    return (
      `M ${left[0].x} ${left[0].y}` +
      appendSmoothContinuation(left) +
      ` L ${reversedRight[0].x} ${reversedRight[0].y}` +
      appendSmoothContinuation(reversedRight) +
      ' Z'
    );
  };

  const getBoundarySideScreenPoints = (
    target: BoundaryEditTarget,
    side: BoundarySide,
  ) => {
    const segments =
      target === 'lumen'
        ? refLumenOutlineSegments.current
        : refWallOutlineSegments.current;
    return side === 'left' ? (segments[0] ?? []) : (segments[1] ?? []);
  };

  const getBoundarySideBaseScreenPoints = (
    target: BoundaryEditTarget,
    side: BoundarySide,
  ) => {
    const segments =
      target === 'lumen'
        ? refBaseLumenOutlineSegments.current
        : refBaseWallOutlineSegments.current;
    return side === 'left' ? (segments[0] ?? []) : (segments[1] ?? []);
  };

  const findBoundaryScreenHit = (
    center: PathPoints,
    radiusPx: number,
  ): BoundaryScreenHit | null =>
    findBoundaryScreenHitCore({
      center,
      radiusPx,
      targetOrder: activeBoundaryMode === 'plaque' ? ['plaque', 'lumen'] : ['lumen'],
      canEditBoundaryTarget,
      getBoundarySideScreenPoints,
    });

  const findBoundarySculptHit = (center: PathPoints) =>
    findBoundaryScreenHit(center, BOUNDARY_SCULPT_RADIUS_PX);

  const findBoundaryDirectDragHit = (center: PathPoints) =>
    findBoundaryScreenHit(center, BOUNDARY_DIRECT_DRAG_RADIUS_PX);

  const getBoundarySculptStartRegion = (
    point: PathPoints,
  ): BoundarySculptStartRegion => {
    const labelMap = refLabelMap.current;
    const model = refBoundaryModel.current;
    const local = screenPointToLabelMapPixelCore({
      screenPoint: point,
      labelMap,
      baseCorners: refBaseAnnulusCorners.current,
      offset: refDisplayOverlayOffset.current,
    });

    const fallbackHit = findBoundarySculptHit(point);

    if (labelMap && local) {
      const inBounds =
        local.col >= 0 &&
        local.col < labelMap.width &&
        local.row >= 0 &&
        local.row < labelMap.height;
      const mask =
        refLumenBoundaryMask.current ??
        (model ? rasterizeBoundaryModel(model) : null);

      if (
        inBounds &&
        mask &&
        mask[local.row * labelMap.width + local.col] > 0
      ) {
        return 'inside';
      }

      const row = clampValue(
        Math.floor(local.y),
        0,
        Math.max(0, labelMap.height - 1),
      );
      if (
        model &&
        row >= model.rowStart &&
        row <= model.rowEnd &&
        Number.isFinite(model.leftX[row]) &&
        Number.isFinite(model.rightX[row])
      ) {
        if (local.x < model.leftX[row]) return 'outside-left';
        if (local.x > model.rightX[row]) return 'outside-right';
        return 'inside';
      }

      if (fallbackHit) {
        return fallbackHit.side === 'left' ? 'outside-left' : 'outside-right';
      }

      return local.x < labelMap.width * 0.5
        ? 'outside-left'
        : 'outside-right';
    }

    if (fallbackHit) {
      return fallbackHit.side === 'left' ? 'outside-left' : 'outside-right';
    }

    return 'inside';
  };

  const getBoundarySculptDirection = (
    startRegion: BoundarySculptStartRegion,
    side: BoundarySide,
  ) => {
    if (startRegion === 'inside') return side === 'left' ? -1 : 1;
    if (startRegion === 'outside-left') return 1;
    return -1;
  };

  const getScreenAxisForBoundaryRow = (
    model: CprBoundaryModel,
    side: BoundarySide,
    row: number,
  ): BoundaryScreenAxis | null => {
    const xValue = side === 'left' ? model.leftX[row] : model.rightX[row];
    if (!Number.isFinite(xValue)) return null;

    const spacingX = refBoundarySpacing.current.x;
    const spacingY = refBoundarySpacing.current.y;
    const localY = (row + 0.5) * spacingY;
    const p0 = projectLocalPoint(
      xValue * spacingX,
      localY,
      context.renderer,
      getProjectionActor(),
    );
    const p1 = projectLocalPoint(
      (xValue + 1) * spacingX,
      localY,
      context.renderer,
      getProjectionActor(),
    );
    if (!p0 || !p1) return null;

    const axisDx = p1.x - p0.x;
    const axisDy = p1.y - p0.y;
    const axisLen = Math.hypot(axisDx, axisDy);
    if (!Number.isFinite(axisLen) || axisLen < 1e-6) return null;

    return {
      ux: axisDx / axisLen,
      uy: axisDy / axisLen,
      pxPerCol: axisLen,
    };
  };

  const getBoundarySculptAxis = (
    session: BoundarySculptSession,
    target: BoundaryEditTarget,
    side: BoundarySide,
    model: CprBoundaryModel,
    row: number,
  ) =>
    getBoundarySculptAxisCore({
      session,
      target,
      side,
      model,
      row,
      getScreenAxisForBoundaryRow,
    });

  const applyBoundarySculptAtPoint = (center: PathPoints) => {
    const hit = findBoundarySculptHit(center);
    if (!hit) return false;

    const model = getBoundaryModel(hit.target);
    if (!model) return false;

    const session = refBoundarySculptSession.current;
    if (!session) return false;

    if (!session.baseMasks[hit.target]) {
      const currentMask =
        hit.target === 'lumen'
          ? refLumenBoundaryMask.current
          : refBoundaryMask.current;
      session.baseMasks[hit.target] = new Uint8Array(
        currentMask ?? rasterizeBoundaryModel(model),
      );
    }

    const radius = BOUNDARY_SCULPT_RADIUS_PX;
    const minThickness = Math.max(0.5, LOCAL_DEFORM_CONFIG.minThickness);
    let changed = false;
    const changedSides = new Set<BoundarySide>();

    (['left', 'right'] as BoundarySide[]).forEach((side) => {
      const points = getBoundarySideScreenPoints(hit.target, side);
      const basePoints = getBoundarySideBaseScreenPoints(hit.target, side);
      if (!points.length) return;

      const direction = getBoundarySculptDirection(session.startRegion, side);
      let sideChanged = false;

      for (let i = 0; i < points.length; i += 1) {
        const row = model.rowStart + i;
        if (row < model.rowStart || row > model.rowEnd) continue;

        const point = points[i];
        const dx = point.x - center.x;
        const dy = point.y - center.y;
        const dist = Math.hypot(dx, dy);
        if (!Number.isFinite(dist) || dist <= 1e-6 || dist > radius) continue;

        const axis = getBoundarySculptAxis(
          session,
          hit.target,
          side,
          model,
          row,
        );
        if (!axis) continue;

        const deltaCol =
          (direction * (radius - dist) * BOUNDARY_SCULPT_PRESSURE) /
          Math.max(1e-6, axis.pxPerCol);
        if (!Number.isFinite(deltaCol) || Math.abs(deltaCol) < 1e-3) continue;

        if (side === 'left') {
          const rightLimit = Number.isFinite(model.rightX[row])
            ? model.rightX[row] - minThickness
            : model.colCount - 1;
          const next = clampValue(
            model.leftX[row] + deltaCol,
            0,
            Math.max(0, rightLimit),
          );
          if (Math.abs(next - model.leftX[row]) > 1e-4) {
            const appliedDeltaCol = next - model.leftX[row];
            model.leftX[row] = next;
            const screenDx = axis.ux * appliedDeltaCol * axis.pxPerCol;
            const screenDy = axis.uy * appliedDeltaCol * axis.pxPerCol;
            point.x += screenDx;
            point.y += screenDy;
            if (basePoints[i]) {
              basePoints[i].x += screenDx;
              basePoints[i].y += screenDy;
            }
            sideChanged = true;
          }
        } else {
          const leftLimit = Number.isFinite(model.leftX[row])
            ? model.leftX[row] + minThickness
            : 0;
          const next = clampValue(
            model.rightX[row] + deltaCol,
            Math.min(model.colCount - 1, leftLimit),
            model.colCount - 1,
          );
          if (Math.abs(next - model.rightX[row]) > 1e-4) {
            const appliedDeltaCol = next - model.rightX[row];
            model.rightX[row] = next;
            const screenDx = axis.ux * appliedDeltaCol * axis.pxPerCol;
            const screenDy = axis.uy * appliedDeltaCol * axis.pxPerCol;
            point.x += screenDx;
            point.y += screenDy;
            if (basePoints[i]) {
              basePoints[i].x += screenDx;
              basePoints[i].y += screenDy;
            }
            sideChanged = true;
          }
        }
      }

      if (sideChanged) {
        changed = true;
        changedSides.add(side);
      }
    });

    if (!changed) return false;

    refLastEditedBoundaryTarget.current = hit.target;
    session.modifiedTargets.add(hit.target);
    session.lastSideByTarget[hit.target] =
      changedSides.size === 1 ? Array.from(changedSides)[0] : hit.side;
    const modifiedSides = (session.modifiedSidesByTarget[hit.target] ??= new Set<BoundarySide>());
    changedSides.forEach((side) => modifiedSides.add(side));
    requestForceUpdate();
    return true;
  };

  const beginBoundaryDrag = (
    target: BoundaryEditTarget,
    side: BoundarySide,
    anchorRow: number,
    startClientX: number,
    startClientY: number,
  ) => {
    if (!canEditBoundaryTarget(target)) return;

    const model = getBoundaryModel(target);
    if (!model) return;

    const clampedRow = Math.max(
      model.rowStart,
      Math.min(model.rowEnd, anchorRow),
    );

    const xValue =
      side === 'left' ? model.leftX[clampedRow] : model.rightX[clampedRow];

    if (!Number.isFinite(xValue)) return;

    (window as any).__CPR_BOUNDARY_EDITING__ = true;

    const svgPoint = toSVGPoint({
      clientX: startClientX,
      clientY: startClientY,
    });

    const renderer = context.renderer;
    const paintActor = getProjectionActor();
    const spacingX = refBoundarySpacing.current.x;
    const spacingY = refBoundarySpacing.current.y;
    const localY = (clampedRow + 0.5) * spacingY;

    const p0 = projectLocalPoint(
      xValue * spacingX,
      localY,
      renderer,
      paintActor,
    );
    const p1 = projectLocalPoint(
      (xValue + 1) * spacingX,
      localY,
      renderer,
      paintActor,
    );

    const axisDx = (p1?.x ?? 1) - (p0?.x ?? 0);
    const axisDy = (p1?.y ?? 0) - (p0?.y ?? 0);
    const axisLen = Math.hypot(axisDx, axisDy) || 1;

    refLastEditedBoundaryTarget.current = target;
    refDraggingControl.current = { target, side, index: -1 };
    refBoundaryDragSession.current = {
      target,
      side,
      index: -1,
      anchorRow: clampedRow,
      startScreenX: svgPoint.x,
      startScreenY: svgPoint.y,
      axisUx: axisDx / axisLen,
      axisUy: axisDy / axisLen,
      axisPxPerCol: axisLen,
      baseLeftX: model.leftX.slice(),
      baseRightX: model.rightX.slice(),
      baseMask: rasterizeBoundaryModel(model),
    };

    window.addEventListener('mousemove', handleBoundaryControlDrag, {
      passive: true,
    });
    window.addEventListener('mouseup', handleBoundaryControlUp);
  };

  const handleUpdatePath = (
    newPath: { x: number; y: number }[],
    bandWidth = 20,
  ) => {
    path.current = newPath ?? [];

    if (!newPath || newPath.length < 2) {
      refPerpendicularLines.current = [];
      refPathPoints.current = [];
      refPolygonPoints.current = [];
      refLumenOutlineSegments.current = [];
      refWallOutlineSegments.current = [];
      refLumenControlScreen.current = { left: [], right: [] };
      refControlScreen.current = { left: [], right: [] };
      forceUpdate();
      return;
    }

    const perfLabel = `[CPRPath:${mainIndex}:${mode}:${Date.now()}]`;
    console.time(perfLabel);

    runWorkerTask({
      type: 'perpendicularLine',
      path: newPath,
      bandWidth,
    })
      .then(({ output }: { output: ArrayBuffer }) => {
        const out32 = new Float32Array(output);
        const out: PerpendicularLine[] = new Array(out32.length / 4);
        const pathOut: PathPoints[] = new Array(out32.length / 4);

        for (let i = 0; i < out.length; i += 1) {
          const i4 = i * 4;
          out[i] = {
            startX: out32[i4 + 0],
            startY: out32[i4 + 1],
            endX: out32[i4 + 2],
            endY: out32[i4 + 3],
          };
          pathOut[i] = {
            x: (out32[i4 + 0] + out32[i4 + 2]) / 2,
            y: (out32[i4 + 1] + out32[i4 + 3]) / 2,
          };
        }

        refPerpendicularLines.current = out;
        refPathPoints.current = pathOut;
        refPolygonPoints.current = getPolyPoints({
          pickPoints: refPickPoints.current,
          perpendicularLines: out,
          numPairs,
        });
        scheduleOutlineUpdate();
        forceUpdate();
        context['svgref'] = refSVG;
      })
      .finally(() => {
      });
  };

  useEffect(() => {
    if (analysisTypeKey !== 'tavi' || mode !== 'stretched') return;
    if (refPerpendicularLines.current.length > 0) return;
    const cprPath = context?.path;
    if (!cprPath || cprPath.length < 2) return;

    const bandw = isAorticMode ? 100 : 20;
    const raf = window.requestAnimationFrame(() => {
      handleUpdatePath(cprPath, bandw);
    });
    return () => window.cancelAnimationFrame(raf);
  }, [
    selectedSeriesId,
    analysisType,
    mode,
    branchId,
    centerlineData,
    context?.path?.length,
    dimensions.width,
    dimensions.height,
  ]);

  const computeOutlineFromLabelData = (perfBase?: string) => {
    if (!isBoundaryDisplayMode) {
      clearCprOutlines();
      forceUpdate();
      return;
    }

    const paintActor = getProjectionActor();
    const paintMapper = paintActor?.getMapper?.();
    const cprActor = context.actorIds['CPRActor'] as any;
    const labelData = context.labelData as any;
    const renderer = context.renderer;

    if (!paintMapper || !labelData || !renderer || !cprActor) {
      clearCprOutlines();
      forceUpdate();
      return;
    }

    const outputWidth = 256;
    const outputHeight = 1024;
    const quantizedAngle = Math.round(Number(context.angle ?? 0) * 10) / 10;

    const outlineCacheKey = [
      selectedSeriesId ?? '',
      mainIndex,
      branchId ?? 'none',
      mode,
      quantizedAngle.toFixed(1),
      outputWidth,
      outputHeight,
      Number(paintMapper.getWidth?.() ?? 0).toFixed(3),
      Number(paintMapper.getHeight?.() ?? 0).toFixed(3),
      Number(labelData.getMTime?.() ?? 0),
      activeCprModificationTool,
      'lumen-wall',
    ].join('|');

    try {
      if (refOutlineCacheKey.current !== outlineCacheKey) {
        const labelMap = exportCPRLabelMap(paintMapper, labelData, {
          outWidthPx: outputWidth,
          outHeightPx: outputHeight,
          useCenterlineRows: false,
          projectionAxis: 'normal',
          maxSlabSamples: 1,
        });

        refBoundarySpacing.current = {
          x: labelMap.widthMM / Math.max(1, labelMap.width),
          y: labelMap.heightMM / Math.max(1, labelMap.height),
        };
        refBoundaryFrame.current = {
          width: labelMap.width,
          widthMM: labelMap.widthMM,
          spacingX: refBoundarySpacing.current.x,
          spacingY: refBoundarySpacing.current.y,
          rows: labelMap.rows as Array<{
            center: Vec3;
            direction: Vec3;
            slabAxis: Vec3;
          }>,
        };

        if (perfBase) console.time(`${perfBase}:trace`);
        const sourceMask = new Uint8Array(labelMap.width * labelMap.height);

        for (let i = 0; i < sourceMask.length; i += 1) {
          const v = labelMap.data[i];
          sourceMask[i] = v === 255 ? 255 : 0;
        }

        const lumenModel = buildBoundaryModelFromLabelMask(
          sourceMask,
          labelMap.width,
          labelMap.height,
          labelMap.width * 0.5,
        );
        const wallModel = null as CprBoundaryModel | null;

        if (!lumenModel) {
          clearCprOutlines();
          forceUpdate();
          return;
        }

        refBoundaryModel.current = lumenModel;
        refWallBoundaryModel.current = wallModel;
        const lumenBoundaryMask = isCprPixelBrushTool
          ? new Uint8Array(sourceMask)
          : rasterizeBoundaryModel(lumenModel);
        refLumenBoundaryMask.current = lumenBoundaryMask;
        refBoundaryMask.current = wallModel
          ? rasterizeBoundaryModel(wallModel)
          : null;
        refInitialLumenMask.current = new Uint8Array(lumenBoundaryMask);
        refInitialWallMask.current = refBoundaryMask.current
          ? new Uint8Array(refBoundaryMask.current)
          : null;
        refCumulativeChangedMaskIndices.current = new Set();
        refLumenAddedMaskIndices.current = new Set();
        refLumenRemovedMaskIndices.current = new Set();
        refLumenAddedVoxelIJK.current = [];
        refLumenRemovedVoxelIJK.current = [];
        refLumenAppliedOriginalValues.current.clear();

        refLabelMap.current = {
          width: labelMap.width,
          height: labelMap.height,
          spacingX: refBoundarySpacing.current.x,
          spacingY: refBoundarySpacing.current.y,
          data: new Uint8Array(labelMap.data),
        };
        rebuildLumenSourceCanvas();
        rebuildAnnulusSourceCanvas();

        refLumenUndoStack.current = [
          {
            model: cloneBoundaryModel(lumenModel),
            mask: new Uint8Array(lumenBoundaryMask),
          },
        ];
        refLumenRedoStack.current = [];
        refUndoStack.current = wallModel
          ? [
              {
                model: cloneBoundaryModel(wallModel),
                mask: new Uint8Array(
                  refBoundaryMask.current ?? rasterizeBoundaryModel(wallModel),
                ),
              },
            ]
          : [];
        refRedoStack.current = [];
        refOutlineCacheKey.current = outlineCacheKey;
        notifyCprBoundaryEditHistoryChanged();

      }

      if (refBoundaryModel.current || refWallBoundaryModel.current) {
        applyBoundaryModelToScene(renderer, paintActor);
      }
    } catch {
      clearCprOutlines();
    }

    forceUpdate();
  };

  const scheduleOutlineUpdate = () => {
    if (refOutlineTimer.current !== null) {
      window.clearTimeout(refOutlineTimer.current);
      refOutlineTimer.current = null;
    }

    refOutlineTimer.current = window.setTimeout(() => {
      refOutlineTimer.current = null;
      if (refOutlineBusy.current) {
        refOutlineDirty.current = true;
        return;
      }

      refOutlineBusy.current = true;
      const perfBase = `[CPROutline:${mainIndex}:${mode}:${++refOutlinePerfSeq.current}]`;
      console.time(`${perfBase}:total`);
      try {
        computeOutlineFromLabelData(perfBase);
      } finally {
        refOutlineBusy.current = false;
        if (refOutlineDirty.current) {
          refOutlineDirty.current = false;
          scheduleOutlineUpdate();
        }
      }
    }, 120);
  };

  useEffect(() => {
    return subscribeSegmentHistoryChange(({ delta }) => {
      if (!isBoundaryDisplayMode) return;
      if (delta.segmentLabel !== 'coronary') return;

      const sourceId = (delta as any).sourceId as string | undefined;
      if (sourceId === getCprManualEditSourceId()) return;

      refOutlineCacheKey.current = '';
      scheduleOutlineUpdate();
    });
  }, [
    isBoundaryDisplayMode,
    isCprPixelBrushTool,
    mainIndex,
    mode,
    selectedSeriesId,
  ]);

  useEffect(() => {
    scheduleOutlineUpdate();
  }, [
    dimensions.width,
    dimensions.height,
    analysisType,
    mode,
    branchId,
    activeBoundaryMode,
    activeCprModificationTool,
  ]);

  useEffect(() => {
    return () => {
      if (refOutlineTimer.current !== null) {
        window.clearTimeout(refOutlineTimer.current);
        refOutlineTimer.current = null;
      }
      if (forceUpdateRaf.current !== null) {
        window.cancelAnimationFrame(forceUpdateRaf.current);
        forceUpdateRaf.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const paintActor = context.actorIds['CPRPaintActor'] as any;
    if (!paintActor?.setVisibility) return;

    if (isBoundaryDisplayMode) {
      if (refPaintActorOriginalVisibility.current === null) {
        refPaintActorOriginalVisibility.current =
          paintActor.getVisibility?.() ?? true;
      }
      paintActor.setVisibility(false);
      context.renderer?.getRenderWindow?.()?.render?.();
      return;
    }

    if (refPaintActorOriginalVisibility.current !== null) {
      paintActor.setVisibility(refPaintActorOriginalVisibility.current);
      refPaintActorOriginalVisibility.current = null;
      context.renderer?.getRenderWindow?.()?.render?.();
    }
  }, [isBoundaryDisplayMode, context.actorIds]);

  useEffect(() => {
    return () => {
      const paintActor = context.actorIds['CPRPaintActor'] as any;
      if (
        paintActor?.setVisibility &&
        refPaintActorOriginalVisibility.current !== null
      ) {
        paintActor.setVisibility(refPaintActorOriginalVisibility.current);
        refPaintActorOriginalVisibility.current = null;
        context.renderer?.getRenderWindow?.()?.render?.();
      }
    };
  }, []);

  const markerRendererId = `MarkerLine.${mainIndex}.${mode}`;

  useLayoutEffect(() => {
    const rendererEntry = {
      render: (args?: any) => {
        const { type } = args ?? {};

        if (type === 'captureDone') {
          updateCPRState({
            mainIndex: { index: 0, branchId: mainBranchIds[0] },
          });
        }

        if (type === 'updatePath') {
          const { newPath }: { newPath: typeof path.current } = args;
          path.current = newPath;
          const bandw = isAorticMode ? 100 : 20;
          handleUpdatePath(newPath, bandw);
          if (
            (refBoundaryModel.current || refWallBoundaryModel.current) &&
            isBoundaryDisplayMode
          ) {
            applyBoundaryModelToScene(context.renderer, getProjectionActor());
          }
        }

        if (type === 'panelmove') {
          if (
            (refBoundaryModel.current || refWallBoundaryModel.current) &&
            isBoundaryDisplayMode
          ) {
            const currentOverlay = getOverlayScreenPosition();
            const prevOverlay = refLastOverlayScreen.current;
            if (prevOverlay) {
              shiftOverlayGeometryBy(
                currentOverlay.x - prevOverlay.x,
                currentOverlay.y - prevOverlay.y,
              );
            } else {
              applyBoundaryModelToScene(context.renderer, getProjectionActor());
            }
            refLastOverlayScreen.current = currentOverlay;
          } else {
            scheduleOutlineUpdate();
          }
          forceUpdate();
        }

        if (type === 'reproject') {
          if (isBoundaryDisplayMode) {
            refOutlineCacheKey.current = '';
            scheduleOutlineUpdate();
          } else {
            forceUpdate();
          }
        }

        if (type === 'markerMove') {
          const { index, which } = args;
          if (!hasValidCenterline(centerlineData)) return;
          if (!refPerpendicularLines.current.length) return;
          if (!Number.isFinite(index) || !Number.isFinite(which)) return;
          const numPts = getSafeCenterlineNumPoints(centerlineData);
          if (!Number.isFinite(numPts) || numPts <= 0) return;
          let expandindex = index;
          if (!isAorticMode) {
            if (index >= numPts - config.coronary_back_expand) {
              expandindex = numPts - config.coronary_back_expand - 1;
            }
          }
          refPickPoints.current[which] = expandindex;
          refPolygonPoints.current = getPolyPoints({
            pickPoints: refPickPoints.current,
            perpendicularLines: refPerpendicularLines.current,
            numPairs,
          });
          forceUpdate();
        }

        if (type === 'polygonMove') {
          const { pickPoints: picked } = args;
          refPickPoints.current = picked;
          refPolygonPoints.current = getPolyPoints({
            pickPoints: picked,
            perpendicularLines: refPerpendicularLines.current,
            numPairs,
          });
          forceUpdate();
        }
      },
    };

    return registerRenderer(markerRendererId, rendererEntry);
  });

  const handleBoundaryControlDrag = (event: MouseEvent) => {
    const drag = refDraggingControl.current;
    const session = refBoundaryDragSession.current;
    const model = session ? getBoundaryModel(session.target) : null;
    if (!drag || !model || !refSVG.current || !session) return;

    const svgPoint = toSVGPoint({
      clientX: event.clientX,
      clientY: event.clientY,
    });

    const dx = svgPoint.x - session.startScreenX;
    const dy = svgPoint.y - session.startScreenY;
    const projectedDx = dx * session.axisUx + dy * session.axisUy;
    const deltaX = projectedDx / Math.max(1e-6, session.axisPxPerCol);

    const deformed = applyLocalBoundaryDeformation(
      model,
      session.baseLeftX,
      session.baseRightX,
      session.anchorRow,
      deltaX,
      session.side,
      LOCAL_DEFORM_CONFIG,
    );

    model.leftX = deformed.leftX;
    model.rightX = deformed.rightX;
    syncControlPointsFromDense(model, session.side);

    setBoundaryMask(session.target, rasterizeBoundaryModel(model));
    if (session.target === 'plaque') {
      rebuildAnnulusSourceCanvas();
    } else {
      rebuildLumenSourceCanvas();
    }
    applyBoundaryModelToScene(
      context.renderer,
      getProjectionActor(),
    );
    forceUpdate();
  };

  const collectVoxelIJK = (
    maskIndices: Iterable<number>,
    valueForIndex: (idx: number) => number,
  ): VoxelIJKCollection =>
    collectVoxelIJKCore({
      maskIndices,
      valueForIndex,
      frame: refBoundaryFrame.current,
      labelData: context.labelData as any,
      localDeformConfig: LOCAL_DEFORM_CONFIG,
    });

  const applyVoxelChangesToLabelData = (
    changes: VoxelIJKChange[],
    onlyIfCurrentValue?: number,
  ) =>
    applyVoxelChangesToLabelDataCore({
      labelData: context.labelData as any,
      changes,
      originalValues: refLumenAppliedOriginalValues.current,
      onlyIfCurrentValue,
    });

  const restoreLumenLabelDataApplyPreview = () =>
    restoreLumenLabelDataApplyPreviewCore({
      labelData: context.labelData as any,
      originalValues: refLumenAppliedOriginalValues.current,
    });

  const restoreLumenLabelDataApplyPreviewForChanges = (
    changes: VoxelIJKChange[],
  ) =>
    restoreLumenLabelDataApplyPreviewForChangesCore({
      labelData: context.labelData as any,
      originalValues: refLumenAppliedOriginalValues.current,
      changes,
    });

  const getBoundaryRowAxis = (
    row: number,
    frame: BoundaryFrame,
    rowFrame: BoundaryFrame['rows'][number],
  ): Vec3 => getBoundaryRowAxisCore(row, frame, rowFrame);

  const collectSolidFootprintVoxelIJK = (
    maskIndices: Iterable<number>,
    valueForIndex: (idx: number) => number,
    options: SolidFootprintOptions = {},
  ): LabelDataApplyVoxelCollection =>
    collectSolidFootprintVoxelIJKCore({
      maskIndices,
      valueForIndex,
      frame: refBoundaryFrame.current,
      labelData: context.labelData as any,
      options,
    });

  const expandVoxelIJKForLabelApply = (
    source: VoxelIJKChange[],
    value: number,
    radiusVoxels = 1,
  ): LabelDataApplyVoxelCollection =>
    expandVoxelIJKForLabelApplyCore({
      source,
      value,
      labelData: context.labelData as any,
      radiusVoxels,
    });

  const collectLocalCurrentLumenIndices = (
    afterMask: Uint8Array,
    changed: Iterable<number>,
    predicate: (isLumen: boolean) => boolean = (isLumen) => isLumen,
  ) =>
    collectLocalCurrentLumenIndicesCore(
      afterMask,
      changed,
      refBoundaryFrame.current,
      predicate,
    );



  const getCprManualEditSourceId = () => {
    const editSourceType = isCprPixelBrushTool ? 'brush' : 'boundary';
    return `CPR.${selectedSeriesId ?? 'unknown'}.${mainIndex}.${mode}.lumen.${editSourceType}`;
  };

  const ensureManualModelSource = () => {
    const sourceId = getCprManualEditSourceId();
    if (refManualModelSourceId.current !== sourceId) {
      refManualModelSourceId.current = sourceId;
      refManualModelAddedVoxelMap.current.clear();
      refManualModelRemovedVoxelMap.current.clear();
    }
    return sourceId;
  };

  const compactManualModelVoxelIJK = (
    changes: VoxelIJKChange[],
    minWeight = 0.12,
  ) => {
    const out = new Map<string, VoxelIJKChange>();
    changes.forEach((change) => {
      if ((change.weight ?? 1) < minWeight) return;
      out.set(getVoxelKey(change), change);
    });
    return Array.from(out.values());
  };

  const deleteManualModelVoxels = (changes: VoxelIJKChange[]) => {
    changes.forEach((change) => {
      const key = getVoxelKey(change);
      refManualModelAddedVoxelMap.current.delete(key);
      refManualModelRemovedVoxelMap.current.delete(key);
    });
  };

  const applyLumenManualSegmentationEdit = (
    addedVoxelIJK3D: VoxelIJKChange[],
    removedVoxelIJK3D: VoxelIJKChange[],
    reason: 'edit' | 'history',
    pruneVoxelIJK3D: VoxelIJKChange[] = [],
    resetSource = false,
  ) => {
    const sourceId = ensureManualModelSource();
    if (resetSource) {
      refManualModelAddedVoxelMap.current.clear();
      refManualModelRemovedVoxelMap.current.clear();
    }

    deleteManualModelVoxels(pruneVoxelIJK3D);

    const manualRemovedVoxelIJK3D = compactManualModelVoxelIJK(
      removedVoxelIJK3D,
      0,
    );
    manualRemovedVoxelIJK3D.forEach((change) => {
      const key = getVoxelKey(change);
      refManualModelAddedVoxelMap.current.delete(key);
      refManualModelRemovedVoxelMap.current.set(key, { ...change, value: 0 });
    });

    const manualAddedVoxelIJK3D = compactManualModelVoxelIJK(
      addedVoxelIJK3D,
      0.1,
    );
    manualAddedVoxelIJK3D.forEach((change) => {
      const key = getVoxelKey(change);
      refManualModelRemovedVoxelMap.current.delete(key);
      refManualModelAddedVoxelMap.current.set(key, { ...change, value: 255 });
    });

    const result = applyVoxelIJKEditToSegmentManager({
      sourceId,
      segmentLabel: 'coronary',
      added: Array.from(refManualModelAddedVoxelMap.current.values()),
      removed: Array.from(refManualModelRemovedVoxelMap.current.values()),
    });

    if (result) {
      console.log('[CPR Lumen Boundary Edit][Manual 3D Model]', {
        reason,
        sourceAddedVoxelCount3D: addedVoxelIJK3D.length,
        sourceRemovedVoxelCount3D: removedVoxelIJK3D.length,
        sourcePrunedVoxelCount3D: pruneVoxelIJK3D.length,
        compactAddedVoxelCount3D: manualAddedVoxelIJK3D.length,
        compactRemovedVoxelCount3D: manualRemovedVoxelIJK3D.length,
        cumulativeAddedVoxelCount3D: refManualModelAddedVoxelMap.current.size,
        cumulativeRemovedVoxelCount3D: refManualModelRemovedVoxelMap.current.size,
        addedVoxelCount3D: result.addedVoxelCount,
        removedVoxelCount3D: result.removedVoxelCount,
      });
    }
  };

  const updateLumenDiffArrays = (
    afterMask: Uint8Array,
    side?: BoundarySide,
    beforeMaskForApply?: Uint8Array,
  ) => {
    const initialMask = refInitialLumenMask.current;
    const added = new Set<number>();
    const removed = new Set<number>();

    if (initialMask && initialMask.length === afterMask.length) {
      for (let idx = 0; idx < afterMask.length; idx += 1) {
        const wasLumen = initialMask[idx] > 0;
        const isLumen = afterMask[idx] > 0;
        if (!wasLumen && isLumen) added.add(idx);
        if (wasLumen && !isLumen) removed.add(idx);
      }
    }

    refLumenAddedMaskIndices.current = added;
    refLumenRemovedMaskIndices.current = removed;

    const addedCollection = collectVoxelIJK(added, () => 255);
    const removedCollection = collectVoxelIJK(removed, () => 0);
    const changed = new Set<number>([...added, ...removed]);
    const stepChangedForApply = new Set<number>();
    if (
      side &&
      beforeMaskForApply &&
      beforeMaskForApply.length === afterMask.length
    ) {
      for (let idx = 0; idx < afterMask.length; idx += 1) {
        if (beforeMaskForApply[idx] !== afterMask[idx]) {
          stepChangedForApply.add(idx);
        }
      }
    }
    const hasStepReferenceMask =
      !!beforeMaskForApply && beforeMaskForApply.length === afterMask.length;

    const changedForApply =
      hasStepReferenceMask && stepChangedForApply.size > 0
        ? stepChangedForApply
        : changed;

    const stepAddedForModel = new Set<number>();
    const stepRemovedForModel = new Set<number>();
    const modelReferenceMask = hasStepReferenceMask ? beforeMaskForApply : initialMask;

    if (modelReferenceMask && modelReferenceMask.length === afterMask.length) {
      for (let idx = 0; idx < afterMask.length; idx += 1) {
        const wasLumen = modelReferenceMask[idx] > 0;
        const isLumen = afterMask[idx] > 0;
        if (!wasLumen && isLumen) stepAddedForModel.add(idx);
        if (wasLumen && !isLumen) stepRemovedForModel.add(idx);
      }
    }

    const modelAddedChanged = hasStepReferenceMask ? stepAddedForModel : added;
    const modelRemovedChanged = hasStepReferenceMask
      ? stepRemovedForModel
      : removed;
    const modelRemoveIsIntentional =
      modelRemovedChanged.size > 0 &&
      modelRemovedChanged.size >= Math.max(3, Math.floor(modelAddedChanged.size * 0.28));

    const sideForApply = side;
    const clearPreviewVolumeForApply = collectSolidLumenVolumeVoxelIJK({
      mask: afterMask,
      clearMask: initialMask,
      changed: changedForApply,
      value: 0,
      method: sideForApply
        ? `clear-${sideForApply}-side-solid-lumen-volume`
        : 'clear-initial-final-solid-lumen-volume',
      paddingPx: sideForApply ? 3 : 8,
      depthScale: 0.88,
      radialMarginMm: sideForApply ? 0 : Math.max(0.05, refBoundarySpacing.current.x * 0.15),
      edgeInsetPx: sideForApply ? 0 : 0.35,
      rowNeighborSpread: 0,
    });
    const finalSolidLumenForApply = collectSolidLumenVolumeVoxelIJK({
      mask: afterMask,
      changed: changedForApply,
      value: 255,
      method: sideForApply
        ? `final-${sideForApply}-side-solid-lumen-volume-from-edited-cpr-boundary-model`
        : 'final-solid-lumen-volume-from-edited-cpr-boundary-model',
      paddingPx: sideForApply ? 3 : 8,
      depthScale: 0.72,
      radialMarginMm: 0,
      boundaryModel: refBoundaryModel.current,
      leftEdgeInsetPx: sideForApply === 'right' ? 0 : 0.8,
      rightEdgeInsetPx: sideForApply === 'left' ? 0 : 0.25,
      rowNeighborSpread: 0,
    });


    const emptyManualModelCollection: SolidLumenVolumeVoxelCollection = {
      voxelIJK: [],
      elapsedMs: 0,
      sourcePointCount2D: 0,
      changedSeedCount2D: 0,
      config: {
        method: 'manual-model-empty',
        radiusUMm: 0,
        radiusVMm: 0,
        depthRadiusMm: 0,
        sampleStepMm: 0,
      },
    };

    const manualAddedConeForModel = collectConicalLumenDeltaVoxelIJK({
      mask: afterMask,
      referenceMask: modelReferenceMask,
      changed: modelAddedChanged,
      value: 255,
      method: 'manual-model-local-conical-add-from-step-delta',
      side: sideForApply,
      boundaryModel: refBoundaryModel.current,
      paddingPx: sideForApply ? 6 : 9,
      depthScale: 0.46,
      maxDepthRadiusMm: 1.35,
      edgeInsetPx: 0.05,
      rootOverlapPx: 3.25,
    });

    const bridgeSeedForModel = manualAddedConeForModel.voxelIJK.filter(
      (v) => (v.weight ?? 1) >= 0.32,
    );
    const manualAddedBridgeForModel = expandVoxelIJKForLabelApply(
      bridgeSeedForModel,
      255,
      bridgeSeedForModel.length > 0 ? 1 : 0,
    );
    const manualAddedForModel = {
      ...manualAddedConeForModel,
      voxelIJK: mergeVoxelChanges(
        manualAddedConeForModel.voxelIJK,
        manualAddedBridgeForModel.voxelIJK,
      ),
    };

    const manualConePruneVoxelIJKForModel = modelRemoveIsIntentional
      ? collectConicalLumenDeltaVoxelIJK({
          mask: modelReferenceMask ?? afterMask,
          referenceMask: afterMask,
          changed: modelRemovedChanged,
          value: 255,
          method: 'manual-model-prune-previous-added-cone-from-step-delta',
          side: sideForApply,
          paddingPx: sideForApply ? 6 : 9,
          depthScale: 0.82,
          maxDepthRadiusMm: 2.1,
          edgeInsetPx: 0,
          rootOverlapPx: 2.2,
        })
      : emptyManualModelCollection;
    const manualOutsideMaskPruneForModel = modelRemoveIsIntentional
      ? collectManualAddedVoxelsOutsideCurrentBoundary({
          mask: afterMask,
          changed: modelRemovedChanged,
          side: sideForApply,
          method: 'manual-model-prune-added-voxels-outside-current-cpr-boundary',
          paddingPx: sideForApply ? 14 : 18,
          keepMarginPx: 0.15,
        })
      : emptyManualModelCollection;
    const manualPruneVoxelIJKForModel: SolidLumenVolumeVoxelCollection = {
      ...manualConePruneVoxelIJKForModel,
      voxelIJK: mergeVoxelChanges(
        manualConePruneVoxelIJKForModel.voxelIJK,
        manualOutsideMaskPruneForModel.voxelIJK,
      ),
      sourcePointCount2D:
        manualConePruneVoxelIJKForModel.sourcePointCount2D +
        manualOutsideMaskPruneForModel.sourcePointCount2D,
      changedSeedCount2D:
        manualConePruneVoxelIJKForModel.changedSeedCount2D +
        manualOutsideMaskPruneForModel.changedSeedCount2D,
      config: {
        ...manualConePruneVoxelIJKForModel.config,
        method: modelRemoveIsIntentional
          ? 'manual-model-combined-prune-previous-added-and-outside-current-boundary'
          : 'manual-model-empty',
      },
    };

    const manualConicalRemovedForModel = modelRemoveIsIntentional
      ? collectConicalLumenDeltaVoxelIJK({
          mask: afterMask,
          referenceMask: modelReferenceMask,
          changed: modelRemovedChanged,
          value: 0,
          method: 'manual-model-local-conical-remove-from-step-delta',
          side: sideForApply,
          paddingPx: sideForApply ? 6 : 9,
          depthScale: 0.78,
          maxDepthRadiusMm: 2.2,
          edgeInsetPx: 0,
          rootOverlapPx: 0.2,
        })
      : emptyManualModelCollection;

    const finalSolidKeySetForModelRemove = new Set(
      finalSolidLumenForApply.voxelIJK.map((v) => getVoxelKey(v)),
    );
    const solidRemovedForModel = modelRemoveIsIntentional
      ? clearPreviewVolumeForApply.voxelIJK
          .filter((v) => !finalSolidKeySetForModelRemove.has(getVoxelKey(v)))
          .map((v) => ({ ...v, value: 0 }))
      : [];

    const manualRemovedForModel: SolidLumenVolumeVoxelCollection =
      modelRemoveIsIntentional
        ? {
            ...manualConicalRemovedForModel,
            voxelIJK: mergeVoxelChanges(
              manualConicalRemovedForModel.voxelIJK,
              solidRemovedForModel,
            ),
            config: {
              ...manualConicalRemovedForModel.config,
              method:
                'manual-model-conical-remove-plus-cpr-labeldata-clear-envelope',
            },
          }
        : emptyManualModelCollection;

    refLumenAddedVoxelIJK.current = addedCollection.voxelIJK3D;
    refLumenRemovedVoxelIJK.current = removedCollection.voxelIJK3D;

    return {
      addedVoxelIJK3D: refLumenAddedVoxelIJK.current,
      removedVoxelIJK3D: refLumenRemovedVoxelIJK.current,
      addedVoxelIJK3DForApply: finalSolidLumenForApply.voxelIJK,
      removedVoxelIJK3DForApply: clearPreviewVolumeForApply.voxelIJK,
      manualAddedVoxelIJK3DForModel: manualAddedForModel.voxelIJK,
      manualRemovedVoxelIJK3DForModel: manualRemovedForModel.voxelIJK,
      manualPruneVoxelIJK3DForModel: manualPruneVoxelIJKForModel.voxelIJK,
      addedVoxelIJK2D: addedCollection.flatVoxelIJK,
      removedVoxelIJK2D: removedCollection.flatVoxelIJK,
      addedMeta: addedCollection,
      removedMeta: removedCollection,
      addedMetaForApply: {
        voxelIJK: finalSolidLumenForApply.voxelIJK,
        elapsedMs: finalSolidLumenForApply.elapsedMs,
        config: finalSolidLumenForApply.config,
        sourcePointCount2D: finalSolidLumenForApply.sourcePointCount2D,
        guardSourcePointCount2D: finalSolidLumenForApply.changedSeedCount2D,
        addedDiffVoxelCount3D: finalSolidLumenForApply.voxelIJK.length,
        guardVoxelCount3D: 0,
      },
      removedMetaForApply: {
        voxelIJK: clearPreviewVolumeForApply.voxelIJK,
        elapsedMs: clearPreviewVolumeForApply.elapsedMs,
        config: clearPreviewVolumeForApply.config,
        sourcePointCount2D: clearPreviewVolumeForApply.sourcePointCount2D,
        removedDiffVoxelCount3D: clearPreviewVolumeForApply.voxelIJK.length,
      },
    };
  };

  const logBoundaryDiff = (
    target: BoundaryEditTarget,
    side: BoundarySide | undefined,
    beforeMask: Uint8Array,
    afterMask: Uint8Array,
  ) => {
    if (target === 'lumen') {
      const {
        addedVoxelIJK3D,
        removedVoxelIJK3D,
        addedVoxelIJK3DForApply,
        removedVoxelIJK3DForApply,
        manualAddedVoxelIJK3DForModel,
        manualRemovedVoxelIJK3DForModel,
        manualPruneVoxelIJK3DForModel,
        addedVoxelIJK2D,
        removedVoxelIJK2D,
        addedMeta,
        removedMeta,
        addedMetaForApply,
        removedMetaForApply,
      } =
        updateLumenDiffArrays(afterMask, side, beforeMask);

      console.log('[CPR Lumen Boundary Edit][DICOM IJK 3D]', {
        side,
        expansionConfig: addedMeta.config,
        addedSourcePointCount2D: addedMeta.sourcePointCount,
        addedVoxelCount2D: addedVoxelIJK2D.length,
        addedVoxelIJK2D,
        addedVoxelCount3D: addedVoxelIJK3D.length,
        addedVoxelIJK3D,
        addedCalculationMs: addedMeta.elapsedMs,
        removedSourcePointCount2D: removedMeta.sourcePointCount,
        removedVoxelCount2D: removedVoxelIJK2D.length,
        removedVoxelIJK2D,
        removedVoxelCount3D: removedVoxelIJK3D.length,
        removedVoxelIJK3D,
        removedCalculationMs: removedMeta.elapsedMs,
      });

      const applyRegionVoxelIJK = mergeVoxelChanges(
        removedVoxelIJK3DForApply,
        addedVoxelIJK3DForApply,
      );
      const restoredPreviewVoxelCount = side
        ? restoreLumenLabelDataApplyPreviewForChanges(applyRegionVoxelIJK)
        : restoreLumenLabelDataApplyPreview();
      const appliedRemoved = applyVoxelChangesToLabelData(
        removedVoxelIJK3DForApply,
        255,
      );
      const appliedAdded = applyVoxelChangesToLabelData(addedVoxelIJK3DForApply);
      if (restoredPreviewVoxelCount > 0 || appliedAdded > 0 || appliedRemoved > 0) {
        getProjectionActor()?.getMapper?.()?.modified?.();
        context.renderer?.getRenderWindow?.()?.render?.();
      }
      console.log('[CPR Lumen Boundary Edit][Apply LabelData 3D]', {
        side,
        applyExpansionConfig: addedMetaForApply.config,
        restoredPreviewVoxelCount,
        applyAddedSourcePointCount2D: addedMetaForApply.sourcePointCount2D,
        applyGuardSourcePointCount2D: addedMetaForApply.guardSourcePointCount2D,
        applyAddedDiffVoxelCount3D: addedMetaForApply.addedDiffVoxelCount3D,
        applyGuardVoxelCount3D: addedMetaForApply.guardVoxelCount3D,
        applyRemovedSourcePointCount2D: removedMetaForApply.sourcePointCount2D,
        applyRemovedDiffVoxelCount3D:
          removedMetaForApply.removedDiffVoxelCount3D,
        applyAddedVoxelCount3D: addedVoxelIJK3DForApply.length,
        applyRemovedVoxelCount3D: removedVoxelIJK3DForApply.length,
        appliedAddedVoxelCount3D: appliedAdded,
        appliedRemovedVoxelCount3D: appliedRemoved,
        applyAddedCalculationMs: addedMetaForApply.elapsedMs,
        applyRemovedCalculationMs: removedMetaForApply.elapsedMs,
      });

      applyLumenManualSegmentationEdit(
        manualAddedVoxelIJK3DForModel,
        manualRemovedVoxelIJK3DForModel,
        'edit',
        manualPruneVoxelIJK3DForModel,
      );
      return;
    }

    const initialMask = getInitialBoundaryMask(target) ?? beforeMask;
    const changedStepIndices: number[] = [];
    const changedFromInitialIndices: number[] = [];

    if (beforeMask.length === afterMask.length) {
      for (let idx = 0; idx < afterMask.length; idx += 1) {
        const changedFromStepBase = beforeMask[idx] !== afterMask[idx];
        const changedFromInitial = initialMask[idx] !== afterMask[idx];
        if (changedFromStepBase) changedStepIndices.push(idx);
        if (changedFromInitial) {
          refCumulativeChangedMaskIndices.current.add(idx);
          changedFromInitialIndices.push(idx);
        }
      }
    }

    const changedVoxelIJKStep = collectVoxelIJK(
      changedStepIndices,
      (idx) => (afterMask[idx] > 0 ? 255 : 0),
    );
    const changedVoxelIJKFromInitial = collectVoxelIJK(
      changedFromInitialIndices,
      (idx) => (afterMask[idx] > 0 ? 255 : 0),
    );
    const cumulativeChangedVoxelIJK = collectVoxelIJK(
      refCumulativeChangedMaskIndices.current,
      (idx) => (afterMask[idx] > 0 ? 255 : 0),
    );

    console.log('[CPR Boundary Edit][DICOM IJK 3D]', {
      target,
      side,
      expansionConfig: changedVoxelIJKStep.config,
      changedSourcePointCountStep2D: changedVoxelIJKStep.sourcePointCount,
      changedVoxelCountStep2D: changedVoxelIJKStep.flatVoxelIJK.length,
      changedVoxelIJKStep2D: changedVoxelIJKStep.flatVoxelIJK,
      changedVoxelCountStep3D: changedVoxelIJKStep.voxelIJK3D.length,
      changedVoxelIJKStep3D: changedVoxelIJKStep.voxelIJK3D,
      changedCalculationMsStep: changedVoxelIJKStep.elapsedMs,
      changedSourcePointCountFromInitial2D:
        changedVoxelIJKFromInitial.sourcePointCount,
      changedVoxelCountFromInitial2D:
        changedVoxelIJKFromInitial.flatVoxelIJK.length,
      changedVoxelIJKFromInitial2D: changedVoxelIJKFromInitial.flatVoxelIJK,
      changedVoxelCountFromInitial3D:
        changedVoxelIJKFromInitial.voxelIJK3D.length,
      changedVoxelIJKFromInitial3D: changedVoxelIJKFromInitial.voxelIJK3D,
      changedCalculationMsFromInitial: changedVoxelIJKFromInitial.elapsedMs,
      cumulativeChangedVoxelCountNoDup:
        refCumulativeChangedMaskIndices.current.size,
      cumulativeChangedVoxelCount3D: cumulativeChangedVoxelIJK.voxelIJK3D.length,
      cumulativeChangedVoxelIJK3D: cumulativeChangedVoxelIJK.voxelIJK3D,
      cumulativeCalculationMs: cumulativeChangedVoxelIJK.elapsedMs,
    });
  };

  const handleBoundaryControlUp = () => {
    (window as any).__CPR_BOUNDARY_EDITING__ = false;
    if (!refDraggingControl.current) return;

    const session = refBoundaryDragSession.current;
    const target = session?.target;
    const model = target ? getBoundaryModel(target) : null;
    refDraggingControl.current = null;
    refBoundaryDragSession.current = null;

    if (model && session && target) {
      syncControlPointsFromDense(model);
      const afterMask = rasterizeBoundaryModel(model);
      setBoundaryMask(target, afterMask);
      if (target === 'plaque') {
        rebuildAnnulusSourceCanvas();
      } else {
        rebuildLumenSourceCanvas();
      }
      applyBoundaryModelToScene(context.renderer, getProjectionActor());
      pushSnapshot(target);
      forceUpdate();
      logBoundaryDiff(target, session.side, session.baseMask, afterMask);
    }

    window.removeEventListener('mousemove', handleBoundaryControlDrag);
    window.removeEventListener('mouseup', handleBoundaryControlUp);
  };

  const updateBoundarySculptCursor = (event: {
    clientX: number;
    clientY: number;
  }) => {
    if (!refSVG.current || !isBoundaryDisplayMode) return null;
    const svgPoint = toSVGPoint(event);
    refBoundarySculptCursor.current = {
      x: svgPoint.x,
      y: svgPoint.y,
      visible: true,
      active: !!refBoundarySculptSession.current,
    };
    requestForceUpdate();
    return { x: svgPoint.x, y: svgPoint.y };
  };

  const flushBoundarySculptPendingPoint = () => {
    if (refBoundarySculptFrame.current !== null) {
      window.cancelAnimationFrame(refBoundarySculptFrame.current);
      refBoundarySculptFrame.current = null;
    }

    const point = refBoundarySculptPendingPoint.current;
    refBoundarySculptPendingPoint.current = null;
    if (point && refBoundarySculptSession.current) {
      applyBoundarySculptAtPoint(point);
    }
  };

  const scheduleBoundarySculptApply = (point: PathPoints) => {
    refBoundarySculptPendingPoint.current = point;
    if (refBoundarySculptFrame.current !== null) return;

    refBoundarySculptFrame.current = window.requestAnimationFrame(() => {
      refBoundarySculptFrame.current = null;
      const nextPoint = refBoundarySculptPendingPoint.current;
      refBoundarySculptPendingPoint.current = null;
      if (nextPoint && refBoundarySculptSession.current) {
        applyBoundarySculptAtPoint(nextPoint);
      }
    });
  };

  const handleBoundarySculptMove = (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    const point = updateBoundarySculptCursor(event);
    if (!point) return;
    scheduleBoundarySculptApply(point);
  };

  const handleBoundarySculptUp = () => {
    flushBoundarySculptPendingPoint();
    (window as any).__CPR_BOUNDARY_EDITING__ = false;
    const session = refBoundarySculptSession.current;
    refBoundarySculptSession.current = null;
    refBoundarySculptCursor.current = {
      ...refBoundarySculptCursor.current,
      active: false,
    };

    if (session) {
      session.modifiedTargets.forEach((target) => {
        const model = getBoundaryModel(target);
        const baseMask = session.baseMasks[target];
        if (!model || !baseMask) return;

        syncControlPointsFromDense(model);
        const afterMask = rasterizeBoundaryModel(model);
        setBoundaryMask(target, afterMask);
        if (target === 'plaque') {
          rebuildAnnulusSourceCanvas();
        } else {
          rebuildLumenSourceCanvas();
        }
        applyBoundaryModelToScene(context.renderer, getProjectionActor());
        pushSnapshot(target);
        const modifiedSides = session.modifiedSidesByTarget[target];
        const sideForDiff =
          modifiedSides && modifiedSides.size === 1
            ? Array.from(modifiedSides)[0]
            : undefined;
        logBoundaryDiff(
          target,
          sideForDiff,
          baseMask,
          afterMask,
        );
      });
      forceUpdate();
    }

    window.removeEventListener('mousemove', handleBoundarySculptMove);
    window.removeEventListener('mouseup', handleBoundarySculptUp);
  };

  const handleBoundarySculptMouseDown = (
    event: React.MouseEvent<SVGSVGElement>,
  ) => {
    if (!isBoundaryDisplayMode) return;
    if (!isBoundaryLineTool && !isBoundarySculptTool) return;
    if (event.button !== 0) return;
    if (refDraggingControl.current || refBoundarySculptSession.current) return;

    const point = isBoundarySculptTool
      ? updateBoundarySculptCursor(event)
      : toSVGPoint(event);
    if (!point) return;

    if (isBoundaryLineTool) {
      event.preventDefault();
      event.stopPropagation();
      (event.nativeEvent as any).stopImmediatePropagation?.();

      const dragHit = findBoundaryDirectDragHit(point);
      if (!dragHit) return;

      const model = getBoundaryModel(dragHit.target);
      if (!model) return;

      beginBoundaryDrag(
        dragHit.target,
        dragHit.side,
        model.rowStart + dragHit.index,
        event.clientX,
        event.clientY,
      );
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    (event.nativeEvent as any).stopImmediatePropagation?.();

    refBoundarySculptSession.current = {
      startRegion: getBoundarySculptStartRegion(point),
      baseMasks: {},
      modifiedTargets: new Set(),
      lastSideByTarget: {},
      modifiedSidesByTarget: {},
      axisCache: {},
    };
    (window as any).__CPR_BOUNDARY_EDITING__ = true;

    applyBoundarySculptAtPoint(point);

    window.addEventListener('mousemove', handleBoundarySculptMove, {
      passive: false,
    });
    window.addEventListener('mouseup', handleBoundarySculptUp);
  };

  const handleBoundarySculptMouseMove = (
    event: React.MouseEvent<SVGSVGElement>,
  ) => {
    if (!isBoundaryDisplayMode) return;
    if (!isBoundarySculptTool) return;
    if (refBoundarySculptSession.current) return;
    updateBoundarySculptCursor(event);
  };

  const handleBoundarySculptMouseLeave = () => {
    if (refBoundarySculptSession.current) return;
    refBoundarySculptCursor.current = {
      ...refBoundarySculptCursor.current,
      visible: false,
      active: false,
    };
    requestForceUpdate();
  };

  const handleBoundaryControlDown = (
    target: BoundaryEditTarget,
    side: BoundarySide,
    index: number,
    event: React.MouseEvent<SVGCircleElement>,
  ) => {
    if (event.button !== 0) return;
    if (!isBoundaryLineTool) return;
    if (!canEditBoundaryTarget(target)) return;

    event.preventDefault();
    event.stopPropagation();
    (event.nativeEvent as any).stopImmediatePropagation?.();

    const model = getBoundaryModel(target);
    if (!model) return;

    const cp =
      side === 'left'
        ? model.leftControlPoints[index]
        : model.rightControlPoints[index];
    if (!cp) return;

    beginBoundaryDrag(target, side, cp.row, event.clientX, event.clientY);
  };

  const handleBoundaryOutlineDown = (
    target: BoundaryEditTarget,
    side: BoundarySide,
    event: React.MouseEvent<SVGPathElement>,
  ) => {
    if (event.button !== 0) return;
    if (!isBoundaryLineTool) return;
    if (!canEditBoundaryTarget(target)) return;

    event.preventDefault();
    event.stopPropagation();
    (event.nativeEvent as any).stopImmediatePropagation?.();

    const model = getBoundaryModel(target);
    if (!model) return;

    const svgPoint = toSVGPoint({
      clientX: event.clientX,
      clientY: event.clientY,
    });

    const points =
      target === 'lumen'
        ? side === 'left'
          ? (refLumenOutlineSegments.current[0] ?? [])
          : (refLumenOutlineSegments.current[1] ?? [])
        : side === 'left'
          ? (refWallOutlineSegments.current[0] ?? [])
          : (refWallOutlineSegments.current[1] ?? []);

    if (!points.length) return;

    const nearestIndex = findNearestScreenPointIndex(svgPoint, points);
    if (nearestIndex < 0) return;

    const anchorRow = model.rowStart + nearestIndex;
    beginBoundaryDrag(target, side, anchorRow, event.clientX, event.clientY);
  };

  const getUndoRedoTarget = (): BoundaryEditTarget =>
    activeBoundaryMode === 'lumen'
      ? 'lumen'
      : refLastEditedBoundaryTarget.current;

  const applyBoundarySnapshot = (
    target: BoundaryEditTarget,
    snapshot: CprBoundarySnapshot,
  ) => {
    setBoundaryModel(target, cloneBoundaryModel(snapshot.model));
    setBoundaryMask(target, new Uint8Array(snapshot.mask));
    refLastEditedBoundaryTarget.current = target;

    if (target === 'lumen') {
      const labelMap = refLabelMap.current;
      if (labelMap && labelMap.data.length === snapshot.mask.length) {
        labelMap.data = new Uint8Array(snapshot.mask);
      }

      const {
        addedVoxelIJK3D,
        removedVoxelIJK3D,
        addedVoxelIJK3DForApply,
        removedVoxelIJK3DForApply,
        manualAddedVoxelIJK3DForModel,
        manualRemovedVoxelIJK3DForModel,
        manualPruneVoxelIJK3DForModel,
      } = updateLumenDiffArrays(snapshot.mask);
      const restoredPreviewVoxelCount = restoreLumenLabelDataApplyPreview();
      const appliedRemoved = applyVoxelChangesToLabelData(
        removedVoxelIJK3DForApply,
        255,
      );
      const appliedAdded = applyVoxelChangesToLabelData(addedVoxelIJK3DForApply);
      applyLumenManualSegmentationEdit(
        manualAddedVoxelIJK3DForModel,
        manualRemovedVoxelIJK3DForModel,
        'history',
        manualPruneVoxelIJK3DForModel,
        true,
      );
      context.labelData?.getPointData?.().getScalars?.().modified?.();
      context.labelData?.modified?.();
      if (
        restoredPreviewVoxelCount > 0 ||
        appliedAdded > 0 ||
        appliedRemoved > 0
      ) {
        getProjectionActor()?.getMapper?.()?.modified?.();
        context.renderer?.getRenderWindow?.()?.render?.();
      }
      rebuildLumenSourceCanvas();
      drawLumenOverlayCanvas();
    } else {
      rebuildAnnulusSourceCanvas();
    }

    applyBoundaryModelToScene(context.renderer, getProjectionActor());
    refLastOverlayScreen.current = getOverlayScreenPosition();
    forceUpdate();
  };

  const undoBoundaryEdit = () => {
    const target = getUndoRedoTarget();
    if (!canEditBoundaryTarget(target)) return false;

    const undoStack = getUndoStack(target);
    if (undoStack.length <= 1) return false;

    const cur = undoStack.pop()!;
    getRedoStack(target).push(cloneBoundarySnapshot(cur));
    const prev = undoStack[undoStack.length - 1];
    applyBoundarySnapshot(target, prev);
    return true;
  };

  const redoBoundaryEdit = () => {
    const target = getUndoRedoTarget();
    if (!canEditBoundaryTarget(target)) return false;

    const next = getRedoStack(target).pop();
    if (!next) return false;

    getUndoStack(target).push(cloneBoundarySnapshot(next));
    applyBoundarySnapshot(target, next);
    return true;
  };

  const canUndoBoundaryEdit = () => {
    const target = getUndoRedoTarget();
    return canEditBoundaryTarget(target) && getUndoStack(target).length > 1;
  };

  const canRedoBoundaryEdit = () => {
    const target = getUndoRedoTarget();
    return canEditBoundaryTarget(target) && getRedoStack(target).length > 0;
  };

  useCprBoundaryHistoryRegistration({
    mainIndex,
    mode,
    undo: undoBoundaryEdit,
    redo: redoBoundaryEdit,
    canUndo: canUndoBoundaryEdit,
    canRedo: canRedoBoundaryEdit,
    deps: [activeBoundaryMode, activeCprModificationTool, mainIndex, mode],
  });

  const screenPointToLabelMapPixel = (screenPoint: PathPoints) =>
    screenPointToLabelMapPixelCore({
      screenPoint,
      labelMap: refLabelMap.current,
      baseCorners: refBaseAnnulusCorners.current,
      offset: refDisplayOverlayOffset.current,
    });

  const cprScreenPointToWorld = (screenPoint: PathPoints): Vec3 | null =>
    cprScreenPointToWorldCore({
      screenPoint,
      labelMap: refLabelMap.current,
      frame: refBoundaryFrame.current,
      baseCorners: refBaseAnnulusCorners.current,
      offset: refDisplayOverlayOffset.current,
      labelData: context.labelData as any,
    });

  const handleCprCtrlFocusMouseDown = (
    event: React.MouseEvent<SVGSVGElement>,
  ) => {
    if (!event.ctrlKey || event.button !== 0) return false;
    if (!isBoundaryDisplayMode || !isCprBoundaryTarget) return false;

    const point = toSVGPoint(event);
    const world = cprScreenPointToWorld(point);
    if (!world) return false;

    event.preventDefault();
    event.stopPropagation();
    (event.nativeEvent as any).stopImmediatePropagation?.();

    focusLinkedViewsFromCpr(world);
    return true;
  };

  const collectCprPixelBrushMaskIndices = (screenPoint: PathPoints) =>
    collectCprPixelBrushMaskIndicesCore({
      screenPoint,
      labelMap: refLabelMap.current,
      baseCorners: refBaseAnnulusCorners.current,
      offset: refDisplayOverlayOffset.current,
      radiusPx: CPR_PIXEL_BRUSH_RADIUS_PX,
    });

  const applyCprPixelBrushAtPoint = (screenPoint: PathPoints) => {
    const labelMap = refLabelMap.current;
    if (!labelMap) return false;

    const indices = collectCprPixelBrushMaskIndices(screenPoint);
    if (!indices.length) return false;

    const value = CPR_PIXEL_BRUSH_MODE === 'erase' ? 0 : 255;
    const changedIndices = applyCprPixelBrushMaskEdit({
      labelMap,
      indices,
      value,
      onTouchedIndex: (idx) =>
        refCprPixelBrushSession.current?.touchedIndices.add(idx),
    });

    if (!changedIndices.length) return false;

    const voxelCollection = collectSolidFootprintVoxelIJK(
      changedIndices,
      () => value,
      {
        radiusScale: value === 255 ? 0.2 : 0.25,
        depthScale: value === 255 ? 0.35 : 0.4,
      },
    );

    const applied = applyVoxelChangesToLabelData(
      voxelCollection.voxelIJK,
      value === 0 ? 255 : undefined,
    );

    if (value === 255) {
      applyLumenManualSegmentationEdit(voxelCollection.voxelIJK, [], 'edit');
    } else {
      applyLumenManualSegmentationEdit([], voxelCollection.voxelIJK, 'edit');
    }

    rebuildLumenSourceCanvas();
    drawLumenOverlayCanvas();
    refOutlineCacheKey.current = '';

    if (applied > 0) {
      getProjectionActor()?.getMapper?.()?.modified?.();
      context.renderer?.getRenderWindow?.()?.render?.();
    }

    requestForceUpdate();
    return true;
  };

  const updateCprPixelBrushCursor = (event: {
    clientX: number;
    clientY: number;
  }) => {
    const point = toSVGPoint(event);
    refCprPixelBrushCursor.current = {
      x: point.x,
      y: point.y,
      visible: true,
      active: !!refCprPixelBrushSession.current,
    };
    requestForceUpdate();
    return point;
  };

  const handleCprPixelBrushMove = (event: MouseEvent) => {
    const point = updateCprPixelBrushCursor(event);
    if (refCprPixelBrushSession.current) {
      applyCprPixelBrushAtPoint(point);
    }
  };

  const handleCprPixelBrushUp = () => {
    const session = refCprPixelBrushSession.current;
    refCprPixelBrushSession.current = null;
    refCprPixelBrushCursor.current = {
      ...refCprPixelBrushCursor.current,
      active: false,
    };
    window.removeEventListener('mousemove', handleCprPixelBrushMove);
    window.removeEventListener('mouseup', handleCprPixelBrushUp);
    if (session && session.touchedIndices.size > 0) {
      const sourceId = ensureManualModelSource();
      const committed = commitVoxelIJKEditToSegmentManagerHistory(sourceId);
      refManualModelAddedVoxelMap.current.clear();
      refManualModelRemovedVoxelMap.current.clear();
      if (committed) {
        console.log('[CPR Pixel Brush][SegmentManager History]', committed);
      }
      pushCprPixelBrushSnapshot({ resetHistoryBaseline: true });
    }
    requestForceUpdate();
  };

  const handleCprPixelBrushMouseDown = (
    event: React.MouseEvent<SVGSVGElement>,
  ) => {
    if (!isCprPixelBrushTool) return;
    if (event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();
    (event.nativeEvent as any).stopImmediatePropagation?.();

    ensureCprPixelBrushUndoBaseline();

    const point = updateCprPixelBrushCursor(event);
    refCprPixelBrushSession.current = {
      mode: CPR_PIXEL_BRUSH_MODE,
      touchedIndices: new Set(),
    };
    refCprPixelBrushCursor.current = {
      ...refCprPixelBrushCursor.current,
      active: true,
    };
    applyCprPixelBrushAtPoint(point);

    window.addEventListener('mousemove', handleCprPixelBrushMove, {
      passive: true,
    });
    window.addEventListener('mouseup', handleCprPixelBrushUp);
  };

  const handleCprModificationMouseDown = (
    event: React.MouseEvent<SVGSVGElement>,
  ) => {
    if (handleCprCtrlFocusMouseDown(event)) return;
    if (isCprPixelBrushTool) {
      handleCprPixelBrushMouseDown(event);
      return;
    }
    handleBoundarySculptMouseDown(event);
  };

  const handleCprModificationMouseMove = (
    event: React.MouseEvent<SVGSVGElement>,
  ) => {
    if (isCprPixelBrushTool) {
      updateCprPixelBrushCursor(event);
      return;
    }
    handleBoundarySculptMouseMove(event);
  };

  const handleCprModificationMouseLeave = () => {
    if (isCprPixelBrushTool) {
      if (!refCprPixelBrushSession.current) {
        refCprPixelBrushCursor.current = {
          ...refCprPixelBrushCursor.current,
          visible: false,
          active: false,
        };
        requestForceUpdate();
      }
      return;
    }
    handleBoundarySculptMouseLeave();
  };

  const resolveLesionStackMiddleIndex = (indexes: number[]) => {
    if (!isLesionStackMarkerMode || indexes.length < 3) return null;
    const proximalIndex = Number(indexes[0]);
    const distalIndex = Number(indexes[2]);
    if (!Number.isFinite(proximalIndex) || !Number.isFinite(distalIndex)) {
      return null;
    }

    return clampMiddleIndexBetweenBounds(
      (proximalIndex + distalIndex) / 2,
      proximalIndex,
      distalIndex,
    );
  };

  const buildCenterlinePickedPoint = (index: number) => {
    const nodedata = handleNodeChange(centerlineData, index);
    if (nodedata === null) return null;
    const { position, normal } = nodedata;
    return { position, normal, index };
  };

  const syncLesionStackHiddenMiddle = () => {
    const middleIndex = resolveLesionStackMiddleIndex(refPickPoints.current);
    if (middleIndex === null || !Number.isFinite(middleIndex)) return;

    const middlePickedPoint = buildCenterlinePickedPoint(middleIndex);
    if (!middlePickedPoint || branchId === undefined) return;

    refPickPoints.current[1] = middleIndex;
    refPolygonPoints.current = getPolyPoints({
      pickPoints: refPickPoints.current,
      perpendicularLines: refPerpendicularLines.current,
      numPairs,
    });

    updateCPRState({
      indexedState: {
        mainIndex: {
          branchId,
          index: 1,
        },
        pickPoint: middlePickedPoint,
      },
    });

    const oppositeMode = mode === 'stretched' ? 'straightened' : 'stretched';
    scheduleRender(`MarkerLine.${mainIndex}.${oppositeMode}`, {
      type: 'markerMove',
      index: middleIndex,
      which: 1,
    });
    scheduleRender(`CrossSection.${mainIndex}.1`, {
      type: 'markerMove',
      which: 1,
      newIndex: middleIndex,
    });

    if (!isAorticMode) {
      scheduleRender('Surface3D', {
        type: 'markerMove',
        mainIndex,
        which: 1,
        newPickedPoints: [middlePickedPoint],
      });

      scheduleRender('VR', {
        type: 'markerMove',
        mainIndex,
        which: 1,
        newPickedPoints: [middlePickedPoint],
      });
    }
  };

  const handleMarkerMouseDown = (
    index: number,
    event: React.PointerEvent<SVGLineElement>,
  ) => {
    event.stopPropagation();
    event.preventDefault();
    (event.target as Element).setPointerCapture(event.pointerId);
    if (event.button !== 0) return;

    dragMarker.current = [index];
    window.addEventListener('pointermove', handleMarkerMouseMove);
    window.addEventListener('pointerup', handleMarkerMouseUp);
  };

  const handleMarkerMouseUp = () => {
    const which = dragMarker.current[0];
    const index = refPickPoints.current[which];
    scheduleRender(`MarkerLine.${mainIndex}.stretched`, {
      type: 'markerMove',
      index,
      which,
    });
    scheduleRender(`MarkerLine.${mainIndex}.straightened`, {
      type: 'markerMove',
      index,
      which,
    });
    scheduleRender(`CrossSection.${mainIndex}.${dragMarker.current[0]}`, {
      type: 'markerMoveDone',
      which: dragMarker.current[0],
      newIndex: index,
    });
    if (isLesionStackMarkerMode && (which === 0 || which === 2)) {
      syncLesionStackHiddenMiddle();
    }
    dragMarker.current = [];
    window.removeEventListener('pointermove', handleMarkerMouseMove);
    window.removeEventListener('pointerup', handleMarkerMouseUp);
  };

  const handleMarkerMouseMove = (event: PointerEvent) => {
    if (
      dragMarker.current.length !== 1 ||
      !refSVG.current ||
      !refPickPoints.current ||
      !hasValidCenterline(centerlineData) ||
      !refPerpendicularLines.current.length
    ) {
      return;
    }

    const dragIndex = dragMarker.current[0];
    const svgPoint = toSVGPoint(event);
    let pathIndex = findNearestPath(svgPoint, path.current);

    if (pathIndex !== -1 && refPickPoints.current[dragIndex] !== pathIndex) {
      const [p, d] = [
        dragIndex === 0 ? -1 : refPickPoints.current[dragIndex - 1],
        dragIndex === refPickPoints.current.length - 1
          ? path.current.length
          : refPickPoints.current[dragIndex + 1],
      ];

      if (!(p < pathIndex && pathIndex < d)) return;

      if (!isAorticMode) {
        if (pathIndex < config.coronary_front_expand) {
          pathIndex = config.coronary_front_expand;
        }
        const numPts = getSafeCenterlineNumPoints(centerlineData);
        if (numPts <= 0) return;
        if (pathIndex >= numPts - config.coronary_back_expand) {
          pathIndex = numPts - config.coronary_back_expand - 1;
        }
      }

      const nodedata = handleNodeChange(centerlineData, pathIndex);
      if (nodedata === null) return;
      const { position, normal } = nodedata;
      const newPickedPoint = { position, normal, index: pathIndex };

      refPickPoints.current[dragIndex] = pathIndex;
      const modetemp = mode === 'stretched' ? 'straightened' : 'stretched';

      refPolygonPoints.current = getPolyPoints({
        pickPoints: refPickPoints.current,
        perpendicularLines: refPerpendicularLines.current,
        numPairs,
      });
      requestForceUpdate();
      scheduleRender(`MarkerLine.${mainIndex}.${modetemp}`, {
        type: 'markerMove',
        index: pathIndex,
        which: dragIndex,
      });
      scheduleRender(`CrossSection.${mainIndex}.${dragIndex}`, {
        type: 'markerMove',
        which: dragIndex,
        newIndex: pathIndex,
      });

      if (!isAorticMode) {
        scheduleRender('Surface3D', {
          type: 'markerMove',
          mainIndex,
          which: dragIndex,
          newPickedPoints: [newPickedPoint],
        });

        scheduleRender('VR', {
          type: 'markerMove',
          mainIndex,
          which: dragIndex,
          newPickedPoints: [newPickedPoint],
        });

        scheduleRender('Report', {
          type: 'markerMove',
          mainIndex,
          which: dragIndex,
          newIndex: pathIndex - config.coronary_front_expand,
        });

        const plotname =
          analysisType === 'fai' ? `CPR-${mainIndex}-20` : 'Graph';
        scheduleRender(plotname, {
          type: 'markerMove',
          mainIndex,
          which: dragIndex,
          newIndex: pathIndex - config.coronary_front_expand,
        });
      }
    }
  };

  const handlePolygonMouseDown = (
    index: number,
    event: React.PointerEvent<SVGPolygonElement>,
  ) => {
    event.stopPropagation();
    event.preventDefault();
    if (event.button !== 0) return;

    dragMarker.current = Array.from(
      { length: numPairs },
      (_, i) => index * numPairs + i,
    );
    const svgPoint = toSVGPoint(event);
    const pathIndex = findNearestPath(svgPoint, path.current);
    startY.current = pathIndex;

    window.addEventListener('pointermove', handlePolygonDrag, {
      passive: true,
    });
    window.addEventListener('pointerup', handlePolygonMouseUp);
  };

  const handlePolygonMouseUp = () => {
    dragMarker.current.forEach((dragIndex, index) => {
      scheduleRender(`CrossSection.${mainIndex}.${dragIndex}`, {
        type: 'markerMove',
        which: dragMarker.current[index],
        newIndex: refPickPoints.current[dragIndex],
      });
    });

    if (
      isLesionStackMarkerMode &&
      (dragMarker.current.includes(0) || dragMarker.current.includes(2))
    ) {
      syncLesionStackHiddenMiddle();
    }

    dragMarker.current = [];
    startY.current = undefined;
    window.removeEventListener('pointermove', handlePolygonDrag);
    window.removeEventListener('pointerup', handlePolygonMouseUp);
  };

  const handlePolygonDrag = (event: PointerEvent) => {
    if (
      startY.current === undefined ||
      !dragMarker.current.length ||
      !hasValidCenterline(centerlineData) ||
      !refPerpendicularLines.current.length
    ) {
      return;
    }

    const svgPoint = toSVGPoint(event);
    const pathIndex = findNearestPath(svgPoint, path.current);

    if (pathIndex !== -1 && startY.current !== pathIndex) {
      const clampIndex = (n: number) =>
        isAorticMode
          ? Math.max(0, Math.min(path.current.length - 1, n))
          : Math.max(
              config.coronary_front_expand,
              Math.min(
                path.current.length - 1 - config.coronary_back_expand,
                n,
              ),
            );

      const delta = pathIndex - startY.current;
      const newNodes = dragMarker.current.map((i) =>
        clampIndex(refPickPoints.current[i] + delta),
      );

      if (
        newNodes.every(
          (v, i) => refPickPoints.current[dragMarker.current[i]] === v,
        )
      ) {
        return;
      }
      dragMarker.current.forEach((dragIndex, index) => {
        refPickPoints.current[dragIndex] = newNodes[index];
      });

      const updatedPickPoints = refPickPoints.current.slice();
      const modetemp = mode === 'stretched' ? 'straightened' : 'stretched';
      refPolygonPoints.current = getPolyPoints({
        pickPoints: updatedPickPoints,
        perpendicularLines: refPerpendicularLines.current,
        numPairs,
      });
      requestForceUpdate();
      scheduleRender(`MarkerLine.${mainIndex}.${modetemp}`, {
        type: 'polygonMove',
        pickPoints: updatedPickPoints,
      });

      const newPickedPoints = dragMarker.current.map((dragIndex, index) => {
        const newIndex = newNodes[index];
        const nodedata = handleNodeChange(centerlineData, newIndex);
        if (nodedata === null) return null;
        const { position, normal } = nodedata;
        scheduleRender(`CrossSection.${mainIndex}.${dragIndex}`, {
          type: 'markerMove',
          which: dragIndex,
          newIndex,
        });
        return {
          position,
          normal,
          index: isAorticMode
            ? newIndex
            : newIndex - config.coronary_front_expand,
        };
      });

      scheduleRender('Surface3D', {
        type: 'polygonMove',
        mainIndex,
        which: 'total',
        newPickedPoints,
      });
      scheduleRender('VR', {
        type: 'polygonMove',
        mainIndex,
        which: 'total',
        newPickedPoints,
      });
      scheduleRender('Report', {
        type: 'polygonMove',
        mainIndex,
        which: 'total',
        newPickedPoints,
      });

      const plotname = analysisType === 'fai' ? `CPR-${mainIndex}-20` : 'Graph';
      scheduleRender(plotname, {
        type: 'polygonMove',
        mainIndex,
        which: 'total',
        newPickedPoints,
      });

      startY.current = pathIndex;
    }
  };

  const onHover = (inout: 'in' | 'out', index?: number) => {
    refCursor.current = {
      index: inout === 'out' ? 99 : index!,
      cursor: inout === 'out' ? 'default' : 'move',
    };
    forceUpdate();
  };

  const guidelines =
    branchId !== undefined && !isAorticMode && config.branchArea.size > 0
      ? config.branchArea.get(branchId)
      : null;

  const stenosisarea =
    analysisType === 'stenosis' && branchId !== undefined
      ? config.stenosisLesionInfo[branchId]
      : [];

  const israrea = analysisType === 'isr' ? config.isr_data : [];

  const lesionarea =
    analysisType === 'plaque' &&
    config.plaqueStatus.length > 0 &&
    config.plaqueStatus.find((b) => b.vesselIndex === branchId)
      ? config.plaqueStatus.find((b) => b.vesselIndex === branchId)?.lesions
      : null;

  const centerOffsetY =
    mode === 'stretched' && refPathPoints.current.length
      ? dimensions.height / 2 -
        refPathPoints.current[Math.floor(refPathPoints.current.length / 2)].y
      : 0;

  const showLumenBoundaryControls =
    isLumenBoundaryMode &&
    isLumenOutlineVisible &&
    isBoundaryLineTool &&
    canEditBoundaryTarget('lumen');
  const showPlaqueBoundaryControls =
    isBoundaryLineTool && canEditBoundaryTarget('plaque');

  type TaviCprGuide = {
    key: string;
    label: string;
    idx: number;
    color: string;
  };

  const taviCprGuides = React.useMemo(() => {
    if (analysisTypeKey !== 'tavi' || mode !== 'stretched') {
      return [] as TaviCprGuide[];
    }

    const parsed = (pool as Record<string, any> | undefined)?.['tavi.parsed']
      ?.data as Record<string, any> | undefined;
    if (!parsed) return [] as TaviCprGuide[];

    const guideColorByKey: Record<string, string> = {
      annulus: '#ffb347',
      sinus: '#ff6adf',
      sinotubularjunction: '#7dff6a',
    };

    const raw = [
      { key: 'annulus', label: 'Annulus', idx: parsed?.annulus?.centerIdx },
      {
        key: 'sinus',
        label: 'Sinus of Valsalca',
        idx: parsed?.sinus?.centerIdx,
      },
      {
        key: 'sinotubularjunction',
        label: 'Sinotubular Junction',
        idx: parsed?.sinotubularjunction?.centerIdx,
      },
    ];

    const guides = raw
      .map((v) => ({
        ...v,
        idx: Number(v.idx),
      }))
      .filter((v) => Number.isFinite(v.idx) && v.idx >= 0)
      .map((v) => ({
        key: v.key,
        label: v.label,
        idx: Math.floor(v.idx),
        color: guideColorByKey[v.key] ?? '#57ff57',
      }));

    const deduped = new Map<number, TaviCprGuide>();
    for (const g of guides) {
      if (!deduped.has(g.idx)) deduped.set(g.idx, g);
    }
    return Array.from(deduped.values());
  }, [analysisTypeKey, mode, pool]);

  const showTaviGuideLegend =
    analysisTypeKey === 'tavi' && mode === 'stretched' && taviCprGuides.length > 0;

  const taviGuideLegendWidth = React.useMemo(() => {
    if (!showTaviGuideLegend) return 120;
    const maxLabelLen = taviCprGuides.reduce(
      (acc, g) => Math.max(acc, (g.label ?? '').length),
      0,
    );
    return Math.max(120, Math.min(220, 42 + maxLabelLen * 7));
  }, [showTaviGuideLegend, taviCprGuides]);

  if (!context.markerline_visibility) return null;
  if (config.isCapture) return null;

  const liveLumenFillPath =
    showLumenBoundaryFillOverlay && !isCprPixelBrushTool
      ? buildClosedBoundaryFillPath(refLumenOutlineSegments.current)
      : '';
  const useSvgLumenFill = !isCprPixelBrushTool && liveLumenFillPath.length > 0;
  const liveLumenFill = `rgba(${LUMEN_PREVIEW_RGBA[0]}, ${LUMEN_PREVIEW_RGBA[1]}, ${LUMEN_PREVIEW_RGBA[2]}, ${LUMEN_PREVIEW_RGBA[3] / 255})`;

  return (
    <>
      {showLumenBoundaryOverlay && (
        <canvas
          ref={refLumenCanvas}
          width={dimensions.width}
          height={dimensions.height}
          style={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            left: 0,
            top: 0,
            pointerEvents: 'none',
            visibility: useSvgLumenFill ? 'hidden' : 'visible',
          }}
        />
      )}

      {showPlaqueBoundaryOverlay && (
        <canvas
          ref={refAnnulusCanvas}
          width={dimensions.width}
          height={dimensions.height}
          style={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            left: 0,
            top: 0,
            pointerEvents: 'none',
          }}
        />
      )}
      <svg
        ref={refSVG}
        width={dimensions.width}
        height={dimensions.height}
        onMouseDownCapture={handleCprModificationMouseDown}
        onMouseMove={handleCprModificationMouseMove}
        onMouseLeave={handleCprModificationMouseLeave}
        style={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          left: 0,
          top: 0,
          pointerEvents: 'auto',
          cursor: 'default',
        }}
      >
        {liveLumenFillPath && (
          <path
            d={liveLumenFillPath}
            fill={liveLumenFill}
            stroke="none"
            pointerEvents="none"
          />
        )}

        {showLumenBoundaryShapeOverlay &&
          refLumenOutlineSegments.current.length > 0 && (
            <>
              {refLumenOutlineSegments.current.map((seg, i) => {
                const side: BoundarySide = i === 0 ? 'left' : 'right';
                return (
                  <g key={`lumen-outline-${i}`}>
                    {showLumenBoundaryControls && (
                      <path
                        d={buildSmoothOpenPath(seg)}
                        fill="none"
                        stroke="transparent"
                        strokeWidth={14}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        pointerEvents="stroke"
                        style={{ cursor: 'ew-resize' }}
                        onMouseDown={(e) =>
                          handleBoundaryOutlineDown('lumen', side, e)
                        }
                      />
                    )}
                    <path
                      d={buildSmoothOpenPath(seg)}
                      fill="none"
                      stroke={config.colors.lumenoutline}
                      strokeWidth={1.2}
                      strokeLinejoin="round"
                      strokeLinecap="round"
                      pointerEvents="none"
                    />
                  </g>
                );
              })}
            </>
          )}

        {showLumenBoundaryControls &&
          refLumenControlScreen.current.left.map((p, i) => (
            <circle
              key={`lumen-left-cp-${i}`}
              cx={p.x}
              cy={p.y}
              r={2.8}
              fill={config.colors.lumenoutline}
              stroke="#111"
              strokeWidth={0.8}
              opacity={0.65}
              data-cpr-boundary-control="1"
              style={{ cursor: 'ew-resize', pointerEvents: 'auto' }}
              onMouseDown={(e) =>
                handleBoundaryControlDown('lumen', 'left', i, e)
              }
            />
          ))}

        {showLumenBoundaryControls &&
          refLumenControlScreen.current.right.map((p, i) => (
            <circle
              key={`lumen-right-cp-${i}`}
              cx={p.x}
              cy={p.y}
              r={2.8}
              fill={config.colors.lumenoutline}
              stroke="#111"
              strokeWidth={0.8}
              opacity={0.65}
              data-cpr-boundary-control="1"
              style={{ cursor: 'ew-resize', pointerEvents: 'auto' }}
              onMouseDown={(e) =>
                handleBoundaryControlDown('lumen', 'right', i, e)
              }
            />
          ))}

        {showPlaqueBoundaryOverlay &&
          refWallOutlineSegments.current.length > 0 && (
          <>
            {refWallOutlineSegments.current.map((seg, i) => {
              const side: BoundarySide = i === 0 ? 'left' : 'right';
              return (
                <g key={`wall-outline-${i}`}>
                  <path
                    d={buildSmoothOpenPath(seg)}
                    fill="none"
                    stroke="transparent"
                    strokeWidth={14}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    pointerEvents="stroke"
                    style={{ cursor: 'ew-resize' }}
                    onMouseDown={(e) =>
                      handleBoundaryOutlineDown('plaque', side, e)
                    }
                  />
                  <path
                    d={buildSmoothOpenPath(seg)}
                    fill="none"
                    stroke="#ff9f1a"
                    strokeWidth={1.4}
                    strokeLinejoin="round"
                    strokeLinecap="round"
                    pointerEvents="none"
                  />
                </g>
              );
            })}
          </>
          )}

        {showPlaqueBoundaryControls &&
          refControlScreen.current.left.map((p, i) => (
            <circle
              key={`wall-left-cp-${i}`}
              cx={p.x}
              cy={p.y}
              r={2.8}
              fill="#ff9f1a"
              stroke="#111"
              strokeWidth={0.8}
              opacity={0.65}
              data-cpr-boundary-control="1"
              style={{ cursor: 'ew-resize', pointerEvents: 'auto' }}
              onMouseDown={(e) =>
                handleBoundaryControlDown('plaque', 'left', i, e)
              }
            />
          ))}

        {showPlaqueBoundaryControls &&
          refControlScreen.current.right.map((p, i) => (
            <circle
              key={`wall-right-cp-${i}`}
              cx={p.x}
              cy={p.y}
              r={2.8}
              fill="#ff9f1a"
              stroke="#111"
              strokeWidth={0.8}
              opacity={0.65}
              data-cpr-boundary-control="1"
              style={{ cursor: 'ew-resize', pointerEvents: 'auto' }}
              onMouseDown={(e) =>
                handleBoundaryControlDown('plaque', 'right', i, e)
              }
            />
          ))}

        {isBoundarySculptTool && refBoundarySculptCursor.current.visible && (
          <circle
            cx={refBoundarySculptCursor.current.x}
            cy={refBoundarySculptCursor.current.y}
            r={BOUNDARY_SCULPT_RADIUS_PX}
            fill={
              refBoundarySculptCursor.current.active
                ? 'rgba(47,128,255,0.10)'
                : 'rgba(47,128,255,0.04)'
            }
            stroke={BOUNDARY_SCULPT_COLOR}
            strokeWidth={1}
            strokeDasharray="4 3"
            pointerEvents="none"
          />
        )}

        {isCprPixelBrushTool && refCprPixelBrushCursor.current.visible && (
          <circle
            cx={refCprPixelBrushCursor.current.x}
            cy={refCprPixelBrushCursor.current.y}
            r={CPR_PIXEL_BRUSH_RADIUS_PX}
            fill={
              refCprPixelBrushCursor.current.active
                ? CPR_PIXEL_BRUSH_MODE === 'erase'
                  ? 'rgba(255,95,95,0.14)'
                  : 'rgba(46,230,255,0.14)'
                : CPR_PIXEL_BRUSH_MODE === 'erase'
                  ? 'rgba(255,95,95,0.05)'
                  : 'rgba(46,230,255,0.05)'
            }
            stroke={CPR_PIXEL_BRUSH_COLOR}
            strokeWidth={1}
            strokeDasharray="4 3"
            pointerEvents="none"
          />
        )}

        {showLegacyMarkerLineOverlays && (
          <g
            transform={`translate(${overlayTranslation![0]},${
              overlayTranslation![1] + centerOffsetY
            })`}
          >
          {guidelines &&
            config.label.guideline &&
            guidelines.map(([label, rawIndex], i) => (
              <CPRGuideLine
                key={i}
                dimensions={dimensions}
                overlayTranslation={overlayTranslation!}
                path={refPathPoints.current}
                name={label}
                index={rawIndex + config.coronary_front_expand}
              />
            ))}

          {stenosisarea &&
            config.label.stenosisline &&
            stenosisarea.map((data, i) => {
              const [start, _mid, end, val] = data;
              if (val === 0) return null;
              const color =
                val <= 0.24
                  ? config.colors.autoLesion[0]
                  : val <= 0.49
                    ? config.colors.autoLesion[1]
                    : val <= 0.69
                      ? config.colors.autoLesion[2]
                      : config.colors.autoLesion[3];
              return (
                <CPRDrawArea
                  key={i}
                  dimensions={dimensions}
                  overlayTranslation={overlayTranslation!}
                  path={refPathPoints.current}
                  index={[
                    start + config.coronary_front_expand,
                    end + config.coronary_front_expand,
                  ]}
                  linecolor={color}
                  fillcolor={color}
                />
              );
            })}

          {israrea &&
            config.label.isrline &&
            branchId !== undefined &&
            israrea.map((val, i) => {
              if (val === null) return null;
              const data = val.get(branchId);
              if (!data) return null;
              return (
                <CPRDrawArea
                  key={`ISR-${i}`}
                  dimensions={dimensions}
                  overlayTranslation={overlayTranslation!}
                  path={refPathPoints.current}
                  index={data.index}
                  linecolor={config.colors.isrcontent[i]}
                  fillcolor={config.colors.isrcontent[i]}
                />
              );
            })}

          {lesionarea &&
            config.label.lesionline &&
            lesionarea.map((data, i) => {
              if (i > 2) return null;
              const lesion = [data.value[0], data.value[2]] as [number, number];
              return (
                <CPRDrawArea
                  key={`plaque-${i}`}
                  dimensions={dimensions}
                  overlayTranslation={overlayTranslation!}
                  path={refPathPoints.current}
                  index={lesion}
                  linecolor={config.colors.isrcontent[i]}
                  fillcolor={config.colors.isrcontent[i]}
                />
              );
            })}

          {analysisTypeKey === 'tavi' &&
            mode === 'stretched' &&
            taviCprGuides.map((guide, i) => {
              const line = refPerpendicularLines.current[guide.idx];
              if (!line) return null;
              const { startX, startY, endX, endY } = line;
              if (
                !Number.isFinite(startX) ||
                !Number.isFinite(startY) ||
                !Number.isFinite(endX) ||
                !Number.isFinite(endY)
              ) {
                return null;
              }
              const dx = endX - startX;
              const dy = endY - startY;
              const len = Math.hypot(dx, dy);
              if (!Number.isFinite(len) || len < 1e-6) return null;
              const ux = dx / len;
              const uy = dy / len;
              const mx = (startX + endX) * 0.5;
              const my = (startY + endY) * 0.5;
              const halfLen = Math.max(18, Math.min(44, len * 0.26));
              const x1 = mx - ux * halfLen;
              const y1 = my - uy * halfLen;
              const x2 = mx + ux * halfLen;
              const y2 = my + uy * halfLen;

              return (
                <g key={`tavi-cpr-guide-${i}-${guide.key}-${guide.idx}`}>
                  <line
                    x1={x1}
                    y1={y1}
                    x2={x2}
                    y2={y2}
                    stroke={guide.color}
                    strokeWidth={2}
                    pointerEvents="none"
                  />
                </g>
              );
            })}

          {config.label.markerline &&
            refPolygonPoints.current.map((polygonPoint, i) => {
              const index = -(i + 1);
              const hoveredMe = refCursor.current.index === index;
              return (
                <polygon
                  key={`marker.${mode}.${i}`}
                  points={polygonPoint}
                  fill={hoveredMe ? '#FFD700' : config.colors.MarkerArea}
                  fillOpacity={hoveredMe ? 0.3 : 0.1}
                  stroke={hoveredMe ? '#FFD700' : 'none'}
                  strokeWidth={hoveredMe ? 1 : 0}
                  style={{
                    cursor: refCursor.current.cursor,
                    pointerEvents: 'auto',
                  }}
                  onPointerDown={(event) => handlePolygonMouseDown(i, event)}
                  onPointerOver={() => onHover('in', index)}
                  onPointerOut={() => onHover('out')}
                />
              );
            })}

          {config.label.markerline &&
            refPerpendicularLines.current.length > 0 &&
            refPickPoints.current.map((pathIndex, i) => {
              const index = i + 1;
              const hoveredMe = refCursor.current.index === index;
              const lineStrokeWidth = hoveredMe ? 2.5 : 1.5;

              let pathi = pathIndex;
              if (pathi === undefined || Number.isNaN(pathi)) return null;
              if (pathi < 0) pathi = config.coronary_front_expand;
              if (!isAorticMode) {
                if (
                  pathi >=
                  refPerpendicularLines.current.length -
                    config.coronary_back_expand
                ) {
                  pathi =
                    refPerpendicularLines.current.length -
                    1 -
                    config.coronary_back_expand;
                }
              }
              if (!refPerpendicularLines.current[pathi]) return null;

              const { startX, startY, endX, endY } =
                refPerpendicularLines.current[pathi];
              const gap = isAorticMode ? 0 : 5;
              const midX = (startX + endX) / 2;
              const midY = (startY + endY) / 2;
              const dx = endX - startX;
              const dy = endY - startY;
              const len = Math.sqrt(dx * dx + dy * dy);
              const offsetX = len === 0 ? 0 : (dx / len) * gap;
              const offsetY = len === 0 ? 0 : (dy / len) * gap;

              let stroke = config.colors.Stenosis;
              const strokeWidth =
                i % numPairs === 0 || i % numPairs === numPairs - 1 ? '10' : '5';
              if (numPairs !== 1) {
                const resi = i % numPairs;
                if (resi === 0) {
                  stroke = config.colors.Proximal;
                } else if (resi === numPairs - 1) {
                  stroke = config.colors.Distal;
                }
              }

              return (
                <g key={index}>
                  <line
                    x1={startX - offsetX}
                    y1={startY - offsetY}
                    x2={endX + offsetX}
                    y2={endY + offsetY}
                    stroke="transparent"
                    strokeWidth={strokeWidth}
                    onPointerDown={(event) => handleMarkerMouseDown(i, event)}
                    onPointerOver={() => onHover('in', index)}
                    onPointerOut={() => onHover('out')}
                    style={{
                      cursor: refCursor.current.cursor,
                      pointerEvents: 'stroke',
                    }}
                  />
                  <line
                    x1={startX}
                    y1={startY}
                    x2={midX - offsetX}
                    y2={midY - offsetY}
                    stroke={stroke}
                    strokeWidth={lineStrokeWidth}
                    pointerEvents="none"
                  />
                  <line
                    x1={midX + offsetX}
                    y1={midY + offsetY}
                    x2={endX}
                    y2={endY}
                    stroke={stroke}
                    strokeWidth={lineStrokeWidth}
                    pointerEvents="none"
                  />
                </g>
              );
            })}
          </g>
        )}

        {showTaviGuideLegend && (
          <g transform="translate(10, 10)" pointerEvents="none">
            <rect
              x={0}
              y={0}
              width={taviGuideLegendWidth}
              height={taviCprGuides.length * 20 + 10}
              rx={4}
              fill="rgba(0, 0, 0, 0.45)"
              stroke="rgba(87,255,87,0.25)"
              strokeWidth={1}
            />
            {taviCprGuides.map((guide, i) => {
              const y = 16 + i * 20;
              return (
                <g key={`tavi-cpr-guide-legend-${guide.key}-${i}`}>
                  <line
                    x1={8}
                    y1={y - 4}
                    x2={24}
                    y2={y - 4}
                    stroke={guide.color}
                    strokeWidth={2}
                  />
                  <text
                    x={30}
                    y={y}
                    fill={guide.color}
                    fontSize={12}
                    fontWeight={600}
                    stroke="rgba(0,0,0,0.35)"
                    strokeWidth={0.8}
                    paintOrder="stroke"
                  >
                    {guide.label}
                  </text>
                </g>
              );
            })}
          </g>
        )}
      </svg>
    </>
  );
}
